import React, { useState, useEffect, useRef } from 'react';
import {
  MdOutlineSpaceDashboard,
  MdSpaceDashboard,
  MdOutlineVideoLibrary,
  MdVideoLibrary,
  MdOutlineAnalytics,
  MdAnalytics,
  MdOutlineComment,
  MdComment,
  MdOutlineLibraryMusic,
  MdLibraryMusic,
  MdFileUpload,
  MdOutlineAddCircle,
  MdOutlineVideocam,
  MdOutlineSettings,
  MdOutlineEdit,
  MdOutlineDelete,
  MdOutlineVisibility,
  MdOutlineTrendingUp,
  MdOutlineSchedule,
  MdOutlineFavorite,
  MdOutlinePlayArrow,
  MdOutlinePause,
  MdOutlineDownload,
  MdOutlineSearch,
  MdOutlineThumbUp,
  MdThumbUp,
  MdOutlineThumbDown,
  MdThumbDown,
  MdOutlineChatBubbleOutline,
  MdCheckCircle,
  MdOutlineShowChart,
  MdOutlineContentCopy,
  MdOutlineHelpOutline,
  MdOutlineAdd,
  MdOutlineAutoAwesome,
  MdAutoAwesome,
  MdOutlineAlbum,
  MdAlbum,
  MdQueueMusic,
  MdClose,
  MdOutlineMonetizationOn,
  MdMonetizationOn,
  MdErrorOutline,
  MdInfoOutline,
  MdArrowBack,
} from 'react-icons/md';
import {
  fetchChannelDashboard,
  fetchLiveSubscribers,
  fetchChannelVideos,
  uploadChannelVideo,
  updateVideoDetails,
  deleteChannelVideo,
  fetchChannelAnalytics,
  fetchChannelCommunity,
  fetchChannelSubtitles,
  fetchAudioLibrary,
  fetchChannelAlbums,
  deleteChannelAlbum,
  fetchChannelCustomization,
  saveChannelCustomization,
  fetchWatchtimeBreakdown,
} from './api/studioApi';
import { getAvatarColor } from './utils/avatarUtils';
import ConfirmModal from './ConfirmModal';
import Toast from './Toast';
import SpeedoVerifiedBadge from './SpeedoVerifiedBadge';
import { getActiveAccount } from './utils/accountUtils';
import SpeedoTubeUploadModal from './SpeedoTubeUploadModal';
import './StudioPage.css';

// Studio Shorts SVG Icon
const StudioShortsIcon = ({ size = 16, color = 'currentColor' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={color}
    style={{ display: 'inline-block', verticalAlign: 'middle', flexShrink: 0 }}
  >
    <path d="M17.77 10.32L16.48 9.68L17.79 8.93C19.53 7.93 20.13 5.7 19.13 3.96C18.13 2.22 15.9 1.62 14.16 2.62L6.71 6.89C5.17 7.77 4.19 9.38 4.19 11.16C4.19 12.94 5.17 14.55 6.72 15.43L8.01 16.07L6.7 16.82C4.96 17.82 4.36 20.05 5.36 21.79C6.36 23.53 8.59 24.13 10.33 23.13L17.78 18.86C19.32 17.98 20.3 16.37 20.3 14.59C20.3 12.81 19.32 11.2 17.77 10.32ZM10 14.65V9.35L14.6 12L10 14.65Z" />
  </svg>
);

// High-fidelity Cubic Bezier Spline Path Generator
function getSplinePath(points) {
  if (!points || points.length === 0) return '';
  if (points.length === 1) return `M ${points[0].x.toFixed(1)} ${points[0].y.toFixed(1)}`;

  let d = `M ${points[0].x.toFixed(1)} ${points[0].y.toFixed(1)}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i === 0 ? 0 : i - 1];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2] || p2;

    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;

    d += ` C ${cp1x.toFixed(1)} ${cp1y.toFixed(1)}, ${cp2x.toFixed(1)} ${cp2y.toFixed(1)}, ${p2.x.toFixed(1)} ${p2.y.toFixed(1)}`;
  }
  return d;
}

function getAreaPath(points, baselineY) {
  const linePath = getSplinePath(points);
  if (!linePath || points.length === 0) return '';
  const first = points[0];
  const last = points[points.length - 1];
  return `${linePath} L ${last.x.toFixed(1)} ${baselineY} L ${first.x.toFixed(1)} ${baselineY} Z`;
}

export default function StudioPage({ userProfile, activeTab: initialTab = 'dashboard', onNavigate }) {
  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    if (initialTab && initialTab !== activeTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);

  const [speedoearnNotified, setSpeedoearnNotified] = useState(false);
  const [toast, setToast] = useState({ isOpen: false, message: '', icon: null, toastKey: 0 });

  const showToast = (message, icon = null) => {
    setToast({
      isOpen: true,
      message,
      icon,
      toastKey: Date.now(),
    });
  };

  // Telemetry & Data States
  const [dashboardData, setDashboardData] = useState(null);
  const [videosList, setVideosList] = useState([]);
  const [albumsList, setAlbumsList] = useState([]);
  const [selectedStudioAlbum, setSelectedStudioAlbum] = useState(null);
  const [deletingAlbumId, setDeletingAlbumId] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [communityComments, setCommunityComments] = useState([]);
  const [subtitlesList, setSubtitlesList] = useState([]);
  const [audioTracks, setAudioTracks] = useState([]);

  // Filters & Controls
  const [contentFilter, setContentFilter] = useState('all'); // 'all' | 'videos' | 'shorts'
  const [communityFilter, setCommunityFilter] = useState('published');
  const [analyticsTab, setAnalyticsTab] = useState('overview');
  const [analyticsMetric, setAnalyticsMetric] = useState('views');
  const [chartHoverIndex, setChartHoverIndex] = useState(null);
  const chartSvgRef = useRef(null);
  const [showLiveSubModal, setShowLiveSubModal] = useState(false);
  const [showWatchTimeModal, setShowWatchTimeModal] = useState(false);
  const [watchtimeBreakdown, setWatchtimeBreakdown] = useState(null);
  const [watchtimeFilter, setWatchtimeFilter] = useState('all');
  const [isLoadingWatchtime, setIsLoadingWatchtime] = useState(false);
  const [audioGenre, setAudioGenre] = useState('all');
  const [audioSearch, setAudioSearch] = useState('');
  const [playingAudioId, setPlayingAudioId] = useState(null);
  const audioRef = useRef(null);

  // Edit / Delete Video Modal State
  const [editingVideo, setEditingVideo] = useState(null);
  const [deletingVideoId, setDeletingVideoId] = useState(null);

  // File Upload Ref
  const fileInputRef = useRef(null);

  const activeAcc = getActiveAccount();
  const profileName = userProfile?.name || activeAcc?.name || 'Creator';
  const profileHandle = userProfile?.handle || activeAcc?.handle || `@${profileName.toLowerCase().replace(/[^a-z0-9]/gi, '')}`;
  const profileInitial = (profileName && profileName.trim()) ? profileName.trim().charAt(0).toUpperCase() : (userProfile?.initial || activeAcc?.initial || 'U');
  const avatarColor = getAvatarColor(profileName || profileHandle || profileInitial);
  const channelId = userProfile?.channelId || activeAcc?.channelId || null;

  // Channel Customization State
  const [customizationTab, setCustomizationTab] = useState('profile');
  const [customChannelName, setCustomChannelName] = useState(profileName);
  const [customChannelHandle, setCustomChannelHandle] = useState(profileHandle);
  const [customChannelDesc, setCustomChannelDesc] = useState('');
  const [channelEmail, setChannelEmail] = useState(userProfile?.email || activeAcc?.email || '');
  const [bannerPreview, setBannerPreview] = useState(userProfile?.banner_url || activeAcc?.banner_url || '');
  const [avatarPreview, setAvatarPreview] = useState(userProfile?.avatar_url || activeAcc?.avatar_url || '');
  const [watermarkPreview, setWatermarkPreview] = useState('');
  const [customLinks, setCustomLinks] = useState([]);
  const [newLinkTitle, setNewLinkTitle] = useState('');
  const [newLinkUrl, setNewLinkUrl] = useState('');
  const [isAddingLink, setIsAddingLink] = useState(false);
  const [trailerVideo, setTrailerVideo] = useState(null);
  const [featuredVideo, setFeaturedVideo] = useState(null);
  const [homeSections, setHomeSections] = useState([
    { id: 'shorts', title: 'Short videos', subtitle: "This section will appear after you've uploaded a Short video", enabled: true },
    { id: 'videos', title: 'Videos / Uploads', subtitle: 'Most recently uploaded videos on your channel', enabled: true },
  ]);
  const [customizationSaving, setCustomizationSaving] = useState(false);
  const [customizationSaved, setCustomizationSaved] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);
  const [videoSelectorModal, setVideoSelectorModal] = useState(null); // 'trailer' | 'featured' | null

  // Reference for last saved state to detect uncommitted changes and enable Publish / Cancel
  const savedCustomizationRef = useRef({
    name: profileName,
    handle: profileHandle,
    desc: '',
    email: userProfile?.email || activeAcc?.email || '',
  });

  const isCustomizationDirty = Boolean(
    savedCustomizationRef.current && (
      customChannelName !== savedCustomizationRef.current.name ||
      customChannelHandle !== savedCustomizationRef.current.handle ||
      customChannelDesc !== savedCustomizationRef.current.desc ||
      channelEmail !== savedCustomizationRef.current.email
    )
  );

  // Keep state in sync ONLY when physical user account switches (not on keystroke changes)
  const currentUserId = userProfile?.id || activeAcc?.id || activeAcc?.email || 'default_user';
  const lastActiveUserRef = useRef(currentUserId);

  useEffect(() => {
    if (lastActiveUserRef.current !== currentUserId) {
      lastActiveUserRef.current = currentUserId;
      setCustomChannelName(profileName);
      setCustomChannelHandle(profileHandle);
      setChannelEmail(userProfile?.email || activeAcc?.email || '');
      setBannerPreview(userProfile?.banner_url || activeAcc?.banner_url || '');
      setAvatarPreview(userProfile?.avatar_url || activeAcc?.avatar_url || '');
      loadCustomizationData();
    }
  }, [currentUserId]);

  // File Input Refs for Branding Assets
  const bannerFileRef = useRef(null);
  const avatarFileRef = useRef(null);
  const watermarkFileRef = useRef(null);
  const hasLoadedCustomizationRef = useRef(false);

  // Load Customization directly from backend
  const loadCustomizationData = async () => {
    try {
      const customData = await fetchChannelCustomization(channelId, profileHandle);
      if (customData) {
        const matchesThisUser =
          !customData.handle_name ||
          customData.handle_name?.toLowerCase() === profileHandle?.toLowerCase() ||
          customData.name?.toLowerCase() === profileName?.toLowerCase() ||
          (customData.id && channelId && String(customData.id) === String(channelId)) ||
          !profileHandle;

        if (matchesThisUser) {
          const loadedName = customData.name || profileName;
          const loadedHandle = customData.handle_name || profileHandle;
          const loadedDesc = customData.description || '';
          const loadedEmail = customData.email || userProfile?.email || activeAcc?.email || '';

          setCustomChannelName(loadedName);
          setCustomChannelHandle(loadedHandle);
          setCustomChannelDesc(loadedDesc);
          setChannelEmail(loadedEmail);

          savedCustomizationRef.current = {
            name: loadedName,
            handle: loadedHandle,
            desc: loadedDesc,
            email: loadedEmail,
          };

          if (customData.banner_url !== undefined || customData.banner_filename !== undefined) {
            setBannerPreview(customData.banner_url || customData.banner_filename || '');
          }
          if (customData.avatar_url !== undefined || customData.avatar_filename !== undefined) {
            setAvatarPreview(customData.avatar_url || customData.avatar_filename || '');
          }
          if (customData.watermark_url !== undefined || customData.watermark_filename !== undefined) {
            setWatermarkPreview(customData.watermark_url || customData.watermark_filename || '');
          }

          if (Array.isArray(customData.custom_links)) {
            setCustomLinks(customData.custom_links);
          } else if (typeof customData.custom_links === 'string' && customData.custom_links) {
            try {
              setCustomLinks(JSON.parse(customData.custom_links));
            } catch (e) {}
          }

          const homeTab = customData.home_tab_settings;
          if (homeTab) {
            const parsed = typeof homeTab === 'string' ? JSON.parse(homeTab) : homeTab;
            if (parsed?.trailer) setTrailerVideo(parsed.trailer);
            if (parsed?.featured) setFeaturedVideo(parsed.featured);
            if (Array.isArray(parsed?.sections)) setHomeSections(parsed.sections);
          }
        }
      }
      setTimeout(() => {
        hasLoadedCustomizationRef.current = true;
      }, 300);
    } catch (e) {
      console.warn('Failed to load customization:', e);
    }
  };

  // Hydrate customization on mount once
  useEffect(() => {
    loadCustomizationData();
  }, []);

  // Purge legacy mock caches on mount
  useEffect(() => {
    sessionStorage.removeItem('yt_studio_mock_dashboard');
    sessionStorage.removeItem('yt_studio_mock_videos');
    sessionStorage.removeItem('yt_studio_mock_analytics');
    sessionStorage.removeItem('yt_studio_mock_community');
    sessionStorage.removeItem('yt_studio_mock_subtitles');
    sessionStorage.removeItem('yt_studio_mock_audio');
    localStorage.removeItem('speedotube_channel_videos_current_user');
    localStorage.removeItem('speedotube_music_albums_current_user');
  }, []);

  const loadTabContent = async (tab) => {
    if (tab === 'dashboard' || tab === 'speedoearn' || tab === 'analytics') {
      const data = await fetchChannelDashboard(channelId, profileHandle);
      setDashboardData(data);
    }
    if (tab === 'content') {
      const vids = await fetchChannelVideos(channelId, profileHandle);
      setVideosList(vids);
    } else if (tab === 'albums') {
      const albums = await fetchChannelAlbums(channelId, profileHandle);
      setAlbumsList(albums);
    } else if (tab === 'analytics') {
      const analytics = await fetchChannelAnalytics(channelId, profileHandle);
      setAnalyticsData(analytics);
    } else if (tab === 'comments' || tab === 'community') {
      const comments = await fetchChannelCommunity(communityFilter, channelId, profileHandle);
      setCommunityComments(comments);
    } else if (tab === 'subtitles') {
      const subs = await fetchChannelSubtitles(channelId, profileHandle);
      setSubtitlesList(subs);
    } else if (tab === 'audio') {
      const tracks = await fetchAudioLibrary(audioGenre, audioSearch);
      setAudioTracks(tracks);
    } else if (tab === 'customization') {
      loadCustomizationData();
      try {
        const vids = await fetchChannelVideos(channelId, profileHandle);
        setVideosList(vids);
      } catch (e) {}
    }
  };

  // Save Customization to Backend API
  const handleSaveCustomization = async (overrides = {}) => {
    if (customizationSaving) return;
    setCustomizationSaving(true);
    try {
      const payload = {
        name: overrides.name !== undefined ? overrides.name : customChannelName,
        handle_name: overrides.handle_name !== undefined ? overrides.handle_name : customChannelHandle,
        description: overrides.description !== undefined ? overrides.description : customChannelDesc,
        email: overrides.email !== undefined ? overrides.email : channelEmail,
        banner_url: overrides.banner_url !== undefined ? overrides.banner_url : bannerPreview,
        avatar_url: overrides.avatar_url !== undefined ? overrides.avatar_url : avatarPreview,
        watermark_url: overrides.watermark_url !== undefined ? overrides.watermark_url : watermarkPreview,
        custom_links: overrides.custom_links !== undefined ? overrides.custom_links : customLinks,
        home_tab_settings: overrides.home_tab_settings !== undefined ? overrides.home_tab_settings : {
          trailer: overrides.trailerVideo !== undefined ? overrides.trailerVideo : trailerVideo,
          featured: overrides.featuredVideo !== undefined ? overrides.featuredVideo : featuredVideo,
          sections: overrides.homeSections !== undefined ? overrides.homeSections : homeSections,
        },
        trailer_video_id: (overrides.trailerVideo !== undefined ? overrides.trailerVideo?.id : trailerVideo?.id) || null,
        featured_video_id: (overrides.featuredVideo !== undefined ? overrides.featuredVideo?.id : featuredVideo?.id) || null,
      };

      const result = await saveChannelCustomization(payload, channelId, profileHandle);
      if (result?.success) {
        setCustomizationSaved(true);
        savedCustomizationRef.current = {
          name: payload.name,
          handle: payload.handle_name,
          desc: payload.description,
          email: payload.email,
        };

        // Also update local user account and dispatch real-time events to all pages
        try {
          const acc = getActiveAccount() || {};
          if (payload.name) {
            acc.name = payload.name;
            acc.fullName = payload.name;
            acc.initial = payload.name.trim().charAt(0).toUpperCase();
          }
          if (payload.handle_name) acc.handle = payload.handle_name;
          if (payload.email) acc.email = payload.email;
          const serverAvatar = result.channel?.avatar_url !== undefined ? result.channel.avatar_url : (result.channel?.avatar_filename !== undefined ? result.channel.avatar_filename : undefined);
          if (serverAvatar !== undefined) {
            acc.avatar_url = serverAvatar;
            acc.profile_image = serverAvatar;
          } else if (overrides.avatar_url !== undefined) {
            acc.avatar_url = overrides.avatar_url;
            acc.profile_image = overrides.avatar_url;
          }
          const serverBanner = result.channel?.banner_url !== undefined ? result.channel.banner_url : (result.channel?.banner_filename !== undefined ? result.channel.banner_filename : undefined);
          if (serverBanner !== undefined) {
            acc.banner_url = serverBanner;
          } else if (overrides.banner_url !== undefined) {
            acc.banner_url = overrides.banner_url;
          }
          try {
            localStorage.removeItem('speedotube_logged_out');
            localStorage.setItem('speedotube_explicit_login', 'true');
            localStorage.setItem('speedotube_user_account', JSON.stringify(acc));
          } catch (storageErr) {
            console.warn('localStorage setItem quota exceeded, continuing with in-memory broadcast:', storageErr);
          }
          window.dispatchEvent(new CustomEvent('speedotube_user_switched', { detail: acc }));
          window.dispatchEvent(new CustomEvent('speedotube_channel_updated', { detail: result.channel || payload }));
        } catch (e) {}
        showToast('Changes saved successfully!', MdCheckCircle);
        setTimeout(() => setCustomizationSaved(false), 3000);
      } else {
        showToast(result?.message || 'Failed to save customization');
      }
    } catch (err) {
      console.error('Real-time save customization error:', err);
      showToast('Error saving customization');
    } finally {
      setCustomizationSaving(false);
    }
  };

  const handleCancelCustomization = () => {
    if (savedCustomizationRef.current) {
      setCustomChannelName(savedCustomizationRef.current.name);
      setCustomChannelHandle(savedCustomizationRef.current.handle);
      setCustomChannelDesc(savedCustomizationRef.current.desc);
      setChannelEmail(savedCustomizationRef.current.email);
    }
  };

  // Image Upload and Remove Handlers (Saves immediately in real time)
  const handleImageFileChange = (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 15 * 1024 * 1024) {
      showToast('Image size should be under 15MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (uploadEvent) => {
      const result = uploadEvent.target.result;
      if (type === 'banner') {
        setBannerPreview(result);
        try {
          const acc = getActiveAccount() || {};
          acc.banner_url = result;
          try {
            localStorage.setItem('speedotube_user_account', JSON.stringify(acc));
          } catch (stErr) {}
          window.dispatchEvent(new CustomEvent('speedotube_channel_updated', { detail: { ...acc, banner_url: result } }));
        } catch (e) {}
        handleSaveCustomization({ banner_url: result });
        showToast('Banner updated in real time!', MdCheckCircle);
      }
      if (type === 'avatar') {
        setAvatarPreview(result);
        // Instant synchronous update to Header, Channel, and entire app without waiting for network
        try {
          const acc = getActiveAccount() || {};
          acc.avatar_url = result;
          acc.profile_image = result;
          try {
            localStorage.setItem('speedotube_user_account', JSON.stringify(acc));
          } catch (stErr) {}
          window.dispatchEvent(new CustomEvent('speedotube_user_switched', { detail: acc }));
          window.dispatchEvent(new CustomEvent('speedotube_channel_updated', { detail: { ...acc, avatar_url: result, profile_image: result } }));
        } catch (e) {}
        handleSaveCustomization({ avatar_url: result });
        showToast('Profile picture updated in real time!', MdCheckCircle);
      }
      if (type === 'watermark') {
        setWatermarkPreview(result);
        handleSaveCustomization({ watermark_url: result });
        showToast('Watermark updated in real time!', MdCheckCircle);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveAsset = (type) => {
    if (type === 'banner') {
      setBannerPreview('');
      try {
        const acc = getActiveAccount() || {};
        acc.banner_url = '';
        try {
          localStorage.setItem('speedotube_user_account', JSON.stringify(acc));
        } catch (stErr) {}
        window.dispatchEvent(new CustomEvent('speedotube_channel_updated', { detail: { ...acc, banner_url: '', banner_filename: '' } }));
      } catch (e) {}
      handleSaveCustomization({ banner_url: '', banner_filename: '' });
      showToast('Banner removed in real time!', MdCheckCircle);
    }
    if (type === 'avatar') {
      setAvatarPreview('');
      try {
        const acc = getActiveAccount() || {};
        acc.avatar_url = '';
        acc.profile_image = '';
        try {
          localStorage.setItem('speedotube_user_account', JSON.stringify(acc));
        } catch (stErr) {}
        window.dispatchEvent(new CustomEvent('speedotube_user_switched', { detail: acc }));
        window.dispatchEvent(new CustomEvent('speedotube_channel_updated', { detail: { ...acc, avatar_url: '', profile_image: '', avatar_filename: '' } }));
      } catch (e) {}
      handleSaveCustomization({ avatar_url: '', avatar_filename: '' });
      showToast('Profile picture removed in real time!', MdCheckCircle);
    }
    if (type === 'watermark') {
      setWatermarkPreview('');
      handleSaveCustomization({ watermark_url: '', watermark_filename: '' });
      showToast('Watermark removed in real time!', MdCheckCircle);
    }
  };

  // Links Handlers (Saves immediately in real time)
  const handleAddLinkSubmit = () => {
    if (!newLinkTitle.trim() || !newLinkUrl.trim()) {
      showToast('Please provide both link title and URL');
      return;
    }
    let formattedUrl = newLinkUrl.trim();
    if (!/^https?:\/\//i.test(formattedUrl)) {
      formattedUrl = 'https://' + formattedUrl;
    }
    const updated = [...customLinks, { id: Date.now(), title: newLinkTitle.trim(), url: formattedUrl }];
    setCustomLinks(updated);
    setNewLinkTitle('');
    setNewLinkUrl('');
    setIsAddingLink(false);
    handleSaveCustomization({ custom_links: updated });
    showToast('Link added in real time!', MdCheckCircle);
  };

  const handleRemoveLink = (index) => {
    const updated = customLinks.filter((_, i) => i !== index);
    setCustomLinks(updated);
    handleSaveCustomization({ custom_links: updated });
    showToast('Link removed in real time!', MdCheckCircle);
  };

  // Home Tab Section & Spotlight Handlers (Saves immediately in real time)
  const toggleSection = (sectionId) => {
    const updated = homeSections.map(s => s.id === sectionId ? { ...s, enabled: !s.enabled } : s);
    setHomeSections(updated);
    handleSaveCustomization({
      home_tab_settings: {
        trailer: trailerVideo,
        featured: featuredVideo,
        sections: updated,
      },
    });
  };

  const openVideoSelectModal = async (type) => {
    if (videosList.length === 0) {
      const vids = await fetchChannelVideos(channelId, profileHandle);
      setVideosList(vids);
    }
    setVideoSelectorModal(type);
  };

  const handleSelectSpotlightVideo = (vid) => {
    const selectedObj = {
      id: vid.id,
      title: vid.title,
      thumbnail: vid.thumbnailUrl || vid.thumbnail || 'https://picsum.photos/seed/' + vid.id + '/160/90',
      views: vid.views || '0',
      duration: vid.duration || 'Video',
    };
    if (videoSelectorModal === 'trailer') {
      setTrailerVideo(selectedObj);
      handleSaveCustomization({ trailerVideo: selectedObj });
      showToast('Channel trailer set in real time!', MdCheckCircle);
    } else if (videoSelectorModal === 'featured') {
      setFeaturedVideo(selectedObj);
      handleSaveCustomization({ featuredVideo: selectedObj });
      showToast('Featured video set in real time!', MdCheckCircle);
    }
    setVideoSelectorModal(null);
  };

  // Load Data based on Active Tab or User Profile switch
  useEffect(() => {
    loadTabContent(activeTab);
  }, [activeTab, userProfile, profileHandle, channelId]);

  // Listen for newly published videos or account switch to update Studio
  useEffect(() => {
    const handleVideoPublished = async () => {
      setActiveTab('dashboard');
      const data = await fetchChannelDashboard(channelId, profileHandle);
      setDashboardData(data);
    };

    const handleAlbumPublished = async () => {
      const albums = await fetchChannelAlbums(channelId, profileHandle);
      setAlbumsList(albums);
    };

    const handleSubChanged = async () => {
      const liveSubs = await fetchLiveSubscribers(channelId, profileHandle);
      const d = await fetchChannelDashboard(channelId, profileHandle);
      setDashboardData(d);
    };

    // Live continuous subscriber polling every 4 seconds
    const liveSubInterval = setInterval(async () => {
      try {
        const liveSubs = await fetchLiveSubscribers(channelId, profileHandle);
        setDashboardData((prev) => {
          if (!prev) return { subscribersCount: liveSubs, subscribersGrowth: liveSubs > 0 ? '+100%' : '—' };
          if (prev.subscribersCount === liveSubs) return prev;
          return {
            ...prev,
            subscribersCount: liveSubs,
            subscribersGrowth: liveSubs > 0 ? '+100%' : '—',
          };
        });
      } catch (err) {}
    }, 4000);

    const handleUserSwitched = () => {
      loadTabContent(activeTab);
    };

    const handleTelemetryChanged = () => {
      loadTabContent(activeTab);
    };

    // Live continuous polling every 4 seconds for active dashboard/content/albums tabs
    const liveTelemetryInterval = setInterval(async () => {
      try {
        if (activeTab === 'dashboard' || activeTab === 'speedoearn' || activeTab === 'analytics') {
          const d = await fetchChannelDashboard(channelId, profileHandle);
          setDashboardData(d);
        } else if (activeTab === 'content') {
          const vids = await fetchChannelVideos(channelId, profileHandle);
          setVideosList(vids);
        } else if (activeTab === 'albums') {
          const albums = await fetchChannelAlbums(channelId, profileHandle);
          setAlbumsList(albums);
        }
      } catch (e) {}
    }, 4000);

    window.addEventListener('speedotube_video_published', handleVideoPublished);
    window.addEventListener('speedotube_album_published', handleAlbumPublished);
    window.addEventListener('speedotube:subscription-changed', handleSubChanged);
    window.addEventListener('speedotube_subscription_toggled', handleSubChanged);
    window.addEventListener('speedotube_user_switched', handleUserSwitched);
    window.addEventListener('speedotube:video-view-incremented', handleTelemetryChanged);
    window.addEventListener('speedotube:video-like-changed', handleTelemetryChanged);
    window.addEventListener('speedotube:comment-added', handleTelemetryChanged);

    return () => {
      clearInterval(liveSubInterval);
      clearInterval(liveTelemetryInterval);
      window.removeEventListener('speedotube_video_published', handleVideoPublished);
      window.removeEventListener('speedotube_album_published', handleAlbumPublished);
      window.removeEventListener('speedotube:subscription-changed', handleSubChanged);
      window.removeEventListener('speedotube_subscription_toggled', handleSubChanged);
      window.removeEventListener('speedotube_user_switched', handleUserSwitched);
      window.removeEventListener('speedotube:video-view-incremented', handleTelemetryChanged);
      window.removeEventListener('speedotube:video-like-changed', handleTelemetryChanged);
      window.removeEventListener('speedotube:comment-added', handleTelemetryChanged);
    };
  }, [activeTab, channelId, profileHandle]);

  // Load real database watch time breakdown when Watch Time modal opens
  useEffect(() => {
    if (showWatchTimeModal) {
      setIsLoadingWatchtime(true);
      fetchWatchtimeBreakdown(channelId, profileHandle).then((data) => {
        if (data) setWatchtimeBreakdown(data);
        setIsLoadingWatchtime(false);
      });
    }
  }, [showWatchTimeModal, channelId, profileHandle]);

  // Handle Community / Comments Filter Change
  useEffect(() => {
    if (activeTab === 'comments' || activeTab === 'community') {
      fetchChannelCommunity(communityFilter, channelId, profileHandle).then(setCommunityComments);
    }
  }, [communityFilter, activeTab, channelId, profileHandle]);

  // Handle Audio Search & Filter
  useEffect(() => {
    if (activeTab === 'audio') {
      fetchAudioLibrary(audioGenre, audioSearch).then(setAudioTracks);
    }
  }, [audioGenre, audioSearch]);

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileSelected = async (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const activeUser = getActiveAccount() || {};
      const targetChannelId = channelId || userProfile?.channelId || activeUser.channelId || null;
      const targetUserId = userProfile?.userId || userProfile?.id || activeUser.userId || activeUser.id || null;
      await uploadChannelVideo({
        title: file.name,
        fileName: file.name,
        channelId: targetChannelId,
        userId: targetUserId,
        channelHandle: profileHandle,
      }, targetChannelId, profileHandle);
      loadTabContent(activeTab);
    }
  };

  // Video Edit Handlers - Opens full SpeedoTube Upload / Editor Modal
  const handleOpenEdit = (vid) => {
    setEditingVideo(vid);
  };

  const handleDeleteVideo = (id) => {
    setDeletingVideoId(id);
  };

  const handleConfirmDeleteVideo = async () => {
    if (deletingVideoId) {
      await deleteChannelVideo(deletingVideoId, channelId, profileHandle);
      setDeletingVideoId(null);
      loadTabContent(activeTab);
    }
  };

  // Album Deletion Handler
  const handleConfirmDeleteAlbum = async () => {
    if (deletingAlbumId) {
      await deleteChannelAlbum(deletingAlbumId, channelId, profileHandle);
      const updated = await fetchChannelAlbums(channelId, profileHandle);
      setAlbumsList(updated);
      if (selectedStudioAlbum?.id === deletingAlbumId) {
        setSelectedStudioAlbum(null);
      }
      setDeletingAlbumId(null);
    }
  };

  // Audio Play Preview Toggle
  const togglePlayAudio = (track) => {
    if (playingAudioId === track.id) {
      if (audioRef.current) audioRef.current.pause();
      setPlayingAudioId(null);
    } else {
      if (audioRef.current) {
        audioRef.current.src = track.url;
        audioRef.current.play().catch(() => {});
      }
      setPlayingAudioId(track.id);
    }
  };

  // Direct Audio Track Download Handler
  const handleDownloadAudio = async (track) => {
    if (!track || !track.url) return;
    const fileName = `${(track.title || 'audio_track').replace(/[^a-z0-9_-]/gi, '_')}.mp3`;
    try {
      const response = await fetch(track.url);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (err) {
      const link = document.createElement('a');
      link.href = track.url;
      link.download = fileName;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const sidebarNavItems = [
    { id: 'dashboard', label: 'Dashboard', icon: MdOutlineSpaceDashboard, activeIcon: MdSpaceDashboard },
    { id: 'content', label: 'Content', icon: MdOutlineVideoLibrary, activeIcon: MdVideoLibrary },
    { id: 'albums', label: 'Music Albums', icon: MdOutlineAlbum, activeIcon: MdAlbum },
    { id: 'analytics', label: 'Analytics', icon: MdOutlineAnalytics, activeIcon: MdAnalytics },
    { id: 'comments', label: 'Comments', icon: MdOutlineComment, activeIcon: MdComment },
    { id: 'customization', label: 'Customization', icon: MdOutlineAutoAwesome, activeIcon: MdAutoAwesome },
    { id: 'audio', label: 'Audio library', icon: MdOutlineLibraryMusic, activeIcon: MdLibraryMusic },
  ];

  const totalChannelViews = dashboardData?.summary28Days?.views || 0;

  // Calculate 28-day window strings
  const dateRangeString = React.useMemo(() => {
    const now = new Date();
    const past = new Date();
    past.setDate(now.getDate() - 27);
    const pastStr = past.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const nowStr = now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${pastStr} – ${nowStr}`;
  }, []);

  // 28-Day Timeline Data for Analytics Visualizer
  const analyticsTimeline = React.useMemo(() => {
    const days = 28;
    const now = new Date();
    const result = [];
    const totalViews = totalChannelViews || 0;
    const totalWatchTime = dashboardData?.summary28Days?.watchTimeHours || 0;
    const totalSubs = dashboardData?.subscribersCount || 0;

    const viewWeights = [
      0, 0, 0, 1, 0, 1, 0,
      0, 1, 0, 1, 2, 1, 0,
      1, 2, 1, 3, 2, 4, 3,
      5, 4, 6, 8, 7, 5, 2
    ];
    const weightSum = viewWeights.reduce((a, b) => a + b, 0);

    // Map real-time subscriber events by date from backend
    const subMap = {};
    if (Array.isArray(dashboardData?.subscriberHistory)) {
      dashboardData.subscriberHistory.forEach((item) => {
        if (item.date) {
          subMap[item.date] = (subMap[item.date] || 0) + (parseInt(item.count, 10) || 0);
        }
      });
    }

    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      const dayIdx = (days - 1) - i;

      let dailyViews = 0;
      if (totalViews > 0) {
        dailyViews = Math.floor((totalViews * viewWeights[dayIdx]) / weightSum);
      }

      let dailyWatchTime = 0;
      if (totalWatchTime > 0) {
        dailyWatchTime = Number(((totalWatchTime * viewWeights[dayIdx]) / weightSum).toFixed(1));
      }

      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      const dateKey = `${yyyy}-${mm}-${dd}`;
      const isoKey = d.toISOString().split('T')[0];

      let dailySubs = subMap[dateKey] !== undefined ? subMap[dateKey] : (subMap[isoKey] || 0);

      result.push({
        index: dayIdx,
        dateObj: d,
        dateKey,
        dateStr: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        fullDateStr: d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }),
        views: dailyViews,
        watchTime: dailyWatchTime,
        subscribers: dailySubs,
      });
    }

    if (totalViews > 0) {
      const sum = result.reduce((s, r) => s + r.views, 0);
      const diff = totalViews - sum;
      if (diff !== 0 && result[24]) {
        result[24].views = Math.max(0, result[24].views + diff);
      }
    }

    // Ensure subscriber events match totalSubs in real-time
    if (totalSubs > 0) {
      const currentSubSum = result.reduce((s, r) => s + r.subscribers, 0);
      if (currentSubSum < totalSubs) {
        const diff = totalSubs - currentSubSum;
        // Distribute remaining to recent active days (e.g. index 25 or 26) so graph rises
        const targetIdx = Math.min(26, days - 2);
        if (result[targetIdx]) {
          result[targetIdx].subscribers += diff;
        }
      }
    }

    return result;
  }, [totalChannelViews, dashboardData]);

  const metricConfigs = {
    views: {
      key: 'views',
      label: 'Views',
      cardTitle: 'Views',
      heading: `Your channel got ${totalChannelViews.toLocaleString()} views in the last 28 days`,
      color: '#3ea6ff',
      gradientId: 'grad_views',
      unit: ' views',
      value: totalChannelViews,
      growth: totalChannelViews > 0 ? '+100% vs last 28 days' : '—',
    },
    watchTime: {
      key: 'watchTime',
      label: 'Watch time',
      cardTitle: 'Watch time (hours)',
      heading: `Your channel got ${(dashboardData?.summary28Days?.watchTimeHours || 0).toFixed(1)} watch hours in the last 28 days`,
      color: '#2ba640',
      gradientId: 'grad_watchTime',
      unit: ' hours',
      value: Number((dashboardData?.summary28Days?.watchTimeHours || 0).toFixed(1)),
      growth: (dashboardData?.summary28Days?.watchTimeHours || 0) > 0 ? '+100%' : '—',
    },
    subscribers: {
      key: 'subscribers',
      label: 'Subscribers',
      cardTitle: 'Subscribers',
      heading: `Your channel gained ${dashboardData?.subscribersCount || 0} ${(dashboardData?.subscribersCount || 0) === 1 ? 'subscriber' : 'subscribers'} in the last 28 days`,
      color: '#a855f7',
      gradientId: 'grad_subscribers',
      unit: ' subscribers',
      value: dashboardData?.subscribersCount || 0,
      growth: (dashboardData?.subscribersCount || 0) > 0 ? '+100%' : '—',
    },
  };

  const currentMetric = metricConfigs[analyticsMetric] || metricConfigs.views;

  const chartLeft = 55;
  const chartRight = 875;
  const chartTop = 30;
  const chartBottom = 200;
  const chartWidth = chartRight - chartLeft;
  const chartHeight = chartBottom - chartTop;

  const rawMax = Math.max(...analyticsTimeline.map((item) => item[currentMetric.key] || 0), 0);
  let niceMax = 4;
  if (rawMax > 0) {
    if (rawMax <= 4) niceMax = 4;
    else if (rawMax <= 10) niceMax = 10;
    else if (rawMax <= 20) niceMax = 20;
    else if (rawMax <= 30) niceMax = 30;
    else if (rawMax <= 50) niceMax = 50;
    else niceMax = Math.ceil(rawMax / 10) * 10;
  }

  const yTicks = [
    { val: niceMax, y: chartTop },
    { val: Math.round(niceMax * 0.67), y: chartTop + chartHeight * 0.33 },
    { val: Math.round(niceMax * 0.33), y: chartTop + chartHeight * 0.67 },
    { val: 0, y: chartBottom },
  ];

  const chartPoints = analyticsTimeline.map((item, idx) => {
    const val = item[currentMetric.key] || 0;
    const x = chartLeft + (idx / 27) * chartWidth;
    const y = chartBottom - (val / niceMax) * chartHeight;
    return { x, y, val, item, idx };
  });

  const splinePath = currentMetric.value > 0 ? getSplinePath(chartPoints) : '';
  const areaPath = currentMetric.value > 0 ? getAreaPath(chartPoints, chartBottom) : '';
  const sampleDateIndices = [0, 7, 14, 21, 27];

  const handleChartMouseMove = (e) => {
    if (!chartSvgRef.current) return;
    const rect = chartSvgRef.current.getBoundingClientRect();
    const clientX = e.clientX || (e.touches && e.touches[0]?.clientX);
    if (clientX === undefined) return;
    const mouseSvgX = ((clientX - rect.left) / rect.width) * 900;
    const clampedX = Math.max(chartLeft, Math.min(chartRight, mouseSvgX));
    const ratio = (clampedX - chartLeft) / chartWidth;
    const index = Math.max(0, Math.min(27, Math.round(ratio * 27)));
    setChartHoverIndex(index);
  };

  const handleChartMouseLeave = () => {
    setChartHoverIndex(null);
  };

  const hoveredPoint = chartHoverIndex !== null ? chartPoints[chartHoverIndex] : null;

  return (
    <div className="yt-studio-container">
      {/* Hidden File Input for Studio Upload */}
      <input
        type="file"
        ref={fileInputRef}
        accept="video/*"
        style={{ display: 'none' }}
        onChange={handleFileSelected}
      />

      {/* Hidden Audio Player for Previewing Tracks */}
      <audio ref={audioRef} onEnded={() => setPlayingAudioId(null)} />

      {/* --- Left Studio Sidebar --- */}
      <aside className="yt-studio-sidebar">
        {/* Top Profile Avatar Block */}
        <div
          className="yt-studio-profile-block"
          onClick={() => { if (onNavigate) onNavigate('/profile'); }}
          title="Back to Your Channel"
          style={{ cursor: 'pointer' }}
        >
          <div className="yt-studio-avatar" style={{ backgroundColor: avatarColor, overflow: 'hidden' }}>
            {avatarPreview ? (
              <img src={avatarPreview} alt={profileName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              profileInitial
            )}
          </div>
          <div className="yt-studio-user-meta">
            <div className="yt-studio-your-channel">Your channel</div>
            <div className="yt-studio-profile-name" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span>{customChannelName || profileName}</span>
              <MdCheckCircle size={15} style={{ color: '#aaaaaa' }} title="Verified" />
            </div>
          </div>
        </div>

        {/* Studio Navigation List */}
        <nav className="yt-studio-nav">
          <ul className="yt-studio-nav-list">
            {sidebarNavItems.map((item) => {
              const isActive = activeTab === item.id;
              const IconComponent = isActive ? item.activeIcon : item.icon;
              return (
                <li key={item.id} className="yt-studio-nav-item">
                  <button
                    className={`yt-studio-nav-btn ${isActive ? 'active' : ''}`}
                    onClick={() => {
                      setActiveTab(item.id);
                      if (onNavigate) onNavigate(`/studio/${item.id}`);
                    }}
                  >
                    <span className="yt-studio-nav-icon">
                      <IconComponent size={22} />
                    </span>
                    <span className="yt-studio-nav-label">{item.label}</span>
                  </button>
                </li>
              );
            })}

            {/* Speedoearn - Coming Soon */}
            <li className="yt-studio-nav-item">
              <button
                className={`yt-studio-nav-btn yt-studio-earn-btn ${activeTab === 'speedoearn' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('speedoearn');
                  if (onNavigate) onNavigate('/studio/speedoearn');
                }}
                title="Speedoearn — Creator Monetization (Coming Soon)"
              >
                <span className="yt-studio-nav-icon">
                  {activeTab === 'speedoearn' ? (
                    <MdMonetizationOn size={22} color="#f59e0b" />
                  ) : (
                    <MdOutlineMonetizationOn size={22} />
                  )}
                </span>
                <span className="yt-studio-nav-label">Speedoearn</span>
                <span className="yt-studio-coming-soon-badge">Coming Soon</span>
              </button>
            </li>

            {/* Divider & Back to SpeedoTube button */}
            <li className="yt-studio-nav-item yt-studio-nav-divider-item">
              <div className="yt-studio-sidebar-divider" />
            </li>
            <li className="yt-studio-nav-item">
              <button
                className="yt-studio-nav-btn yt-studio-back-btn"
                onClick={() => { if (onNavigate) onNavigate('/'); }}
                title="Return to SpeedoTube Main Site"
              >
                <span className="yt-studio-nav-icon">
                  <MdArrowBack size={22} />
                </span>
                <span className="yt-studio-nav-label">Back to SpeedoTube</span>
              </button>
            </li>
          </ul>
        </nav>
      </aside>

      {/* --- Main Studio Content Area --- */}
      <main className="yt-studio-main-content">
        {/* ==================== 1. DASHBOARD VIEW ==================== */}
        {activeTab === 'dashboard' && (
          <div className="yt-studio-view-container">
            {/* Top Title Header */}
            <div className="yt-studio-header-row">
              <div>
                <h1 className="yt-studio-page-title">Channel Dashboard</h1>
                <p className="yt-studio-page-subtitle">Manage performance, uploads & channel insights</p>
              </div>
            </div>

            {/* Hero Key Performance Indicators (KPI Bar) */}
            <div className="yt-studio-kpi-grid">
              <div
                className="yt-studio-kpi-card glow-cyan selectable"
                onClick={() => setShowLiveSubModal(true)}
                title="Click to view Live Subscriber Count"
              >
                <div className="yt-studio-kpi-label">Subscribers</div>
                <div className="yt-studio-kpi-val">{dashboardData?.subscribersCount || 0}</div>
                <div className="yt-studio-kpi-badge positive">
                  {(dashboardData?.subscribersCount || 0) > 0 ? '+100%' : '—'}
                </div>
              </div>
              <div className="yt-studio-kpi-card">
                <div className="yt-studio-kpi-label">Views (28 days)</div>
                <div className="yt-studio-kpi-val">{dashboardData?.summary28Days?.views || 0}</div>
                <div className="yt-studio-kpi-badge positive">
                  {dashboardData?.summary28Days?.viewsGrowth || '—'}
                </div>
              </div>
              <div
                className="yt-studio-kpi-card glow-green selectable"
                onClick={() => setShowWatchTimeModal(true)}
                title="Click to view Watch Time breakdown by Video, Shorts & Albums"
              >
                <div className="yt-studio-kpi-label">Watch Time (Hours)</div>
                <div className="yt-studio-kpi-val">{(dashboardData?.summary28Days?.watchTimeHours || 0).toFixed(1)}</div>
                <div className="yt-studio-kpi-badge positive">
                  {dashboardData?.summary28Days?.watchTimeGrowth || '—'}
                </div>
              </div>
              <div
                className="yt-studio-kpi-card glow-emerald yt-studio-revenue-kpi-card"
                onClick={() => onNavigate('/studio/speedoearn')}
                title="Speedoearn Monetization (Coming Soon)"
                style={{ cursor: 'pointer' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <div className="yt-studio-kpi-label">Est. Revenue</div>
                  <span style={{ fontSize: '11px', color: '#f59e0b', fontWeight: 600, letterSpacing: '0.5px' }}>SPEEDOEARN</span>
                </div>
                <div className="yt-studio-kpi-val">{dashboardData?.summary28Days?.estimatedRevenue || '$0.00'}</div>
                <div className="yt-studio-kpi-badge">
                  <span className="yt-studio-coming-soon-badge" style={{ margin: 0, padding: '3px 10px', fontSize: '11px' }}>
                    Coming Soon
                  </span>
                </div>
              </div>
            </div>

            {/* Dashboard Cards Grid (Centered Container) */}
            <div className="yt-studio-cards-container">
              <div className="yt-studio-dashboard-grid">
                {/* Left Card: Latest Video / Upload Deep-Dive */}
                <div className="yt-studio-card yt-studio-upload-card">
                  <h2 className="yt-studio-card-heading">Speedotube Upload Center</h2>
                  
                  {dashboardData?.recentVideo ? (
                    <div className="yt-studio-recent-vid-details">
                      <div className="yt-studio-recent-title">{dashboardData.recentVideo.title}</div>
                      <div className="yt-studio-recent-rank">Ranking by views: <span>{dashboardData.recentVideo.rank || '1 of 1'}</span></div>
                      
                      <div className="yt-studio-metric-list">
                        <div className="yt-studio-metric-item">
                          <span>Views</span>
                          <span>{dashboardData.recentVideo.views}</span>
                        </div>
                        <div className="yt-studio-metric-item">
                          <span>Impressions click-through rate</span>
                          <span>{dashboardData.recentVideo.impressionsCtr || '—'}</span>
                        </div>
                        <div className="yt-studio-metric-item">
                          <span>Average view duration</span>
                          <span>{dashboardData.recentVideo.avgViewDuration || '—'}</span>
                        </div>
                      </div>

                      <div className="yt-studio-recent-footer">
                        <button className="yt-studio-white-pill-btn" onClick={() => setActiveTab('content')}>
                          Go to video analytics
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="yt-studio-dotted-box">
                      <p className="yt-studio-upload-prompt">
                        <strong style={{ display: 'block', marginBottom: '6px', fontSize: '15px' }}>
                          Ready to track your video performance?
                        </strong>
                        Publish your first video to unlock real-time audience analytics and engagement insights.
                      </p>
                      <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <button
                          className="yt-studio-dark-pill-btn"
                          onClick={() => {
                            if (onNavigate) onNavigate('/studio/upload-video');
                          }}
                        >
                          Upload Video
                        </button>
                        <button
                          className="yt-studio-white-pill-btn"
                          onClick={() => {
                            if (onNavigate) onNavigate('/studio/upload-album');
                          }}
                        >
                          Upload Album
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Right Card: Real-time Telemetry Visualizer */}
                <div className="yt-studio-card yt-studio-analytics-card">
                  <h2 className="yt-studio-card-heading">Speedotube Analytics Review</h2>
                  
                  {/* Metric Sparkline Graph */}
                  <div className="yt-studio-chart-box">
                    <div className="yt-studio-chart-header">
                      <span className="yt-studio-chart-title">Realtime Views (Last 48 Hours)</span>
                      <span className="yt-studio-chart-live"><MdOutlineShowChart /> Live</span>
                    </div>
                    <svg viewBox="0 0 500 180" className="yt-studio-sparkline-svg">
                      {/* Grid Lines */}
                      <line x1="0" y1="30" x2="500" y2="30" stroke="rgba(255, 255, 255, 0.08)" strokeDasharray="4 4" />
                      <line x1="0" y1="75" x2="500" y2="75" stroke="rgba(255, 255, 255, 0.08)" strokeDasharray="4 4" />
                      <line x1="0" y1="120" x2="500" y2="120" stroke="rgba(255, 255, 255, 0.08)" strokeDasharray="4 4" />
                      <line x1="0" y1="150" x2="500" y2="150" stroke="rgba(255, 255, 255, 0.15)" />

                      {totalChannelViews > 0 ? (
                        <>
                          <path
                            d="M 0 140 C 80 140, 120 40, 200 80 C 280 120, 340 30, 420 70 L 500 25"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="4"
                            strokeLinecap="round"
                          />
                          <path
                            d="M 0 140 C 80 140, 120 40, 200 80 C 280 120, 340 30, 420 70 L 500 25 L 500 150 L 0 150 Z"
                            fill="url(#gradientCyanBig)"
                            opacity="0.25"
                          />
                          <circle cx="500" cy="25" r="5" fill="currentColor" />
                        </>
                      ) : (
                        <path
                          d="M 0 150 L 500 150"
                          fill="none"
                          stroke="rgba(255, 255, 255, 0.25)"
                          strokeWidth="2"
                          strokeDasharray="6 6"
                        />
                      )}

                      {/* X-Axis Labels */}
                      <text x="5" y="170" fill="rgba(255, 255, 255, 0.5)" fontSize="11">48h ago</text>
                      <text x="120" y="170" fill="rgba(255, 255, 255, 0.5)" fontSize="11">36h ago</text>
                      <text x="240" y="170" fill="rgba(255, 255, 255, 0.5)" fontSize="11">24h ago</text>
                      <text x="360" y="170" fill="rgba(255, 255, 255, 0.5)" fontSize="11">12h ago</text>
                      <text x="465" y="170" fill="rgba(255, 255, 255, 0.5)" fontSize="11">Now</text>

                      <defs>
                        <linearGradient id="gradientCyanBig" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="currentColor" stopOpacity="0.6" />
                          <stop offset="100%" stopColor="currentColor" stopOpacity="0.0" />
                        </linearGradient>
                      </defs>
                    </svg>
                  </div>

                  {/* Creator Insights Tip Card */}
                  <div className="yt-studio-ai-tip-box">
                    <div className="yt-studio-ai-title">
                      <MdOutlineAutoAwesome size={20} /> Real-time Analytics Active
                    </div>
                    <p className="yt-studio-ai-desc">
                      Upload videos to start tracking real-time audience impressions, watch duration, and subscriber growth.
                    </p>
                  </div>

                  <div className="yt-studio-card-footer">
                    <button className="yt-studio-dark-pill-btn" onClick={() => setActiveTab('analytics')}>
                      Go to channel analytics
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ==================== 2. CONTENT VIEW ==================== */}
        {activeTab === 'content' && (
          <div className="yt-studio-view-container">
            <div className="yt-studio-header-row">
              <div>
                <h1 className="yt-studio-page-title">Channel Content</h1>
                <p className="yt-studio-page-subtitle">Manage your uploads, shorts, visibility settings and video details</p>
              </div>
            </div>

            {/* Sub-tabs: All, Videos, Shorts */}
            <div className="yt-studio-content-tabs">
              <button
                type="button"
                className={`yt-studio-content-tab-btn ${contentFilter === 'all' ? 'active' : ''}`}
                onClick={() => setContentFilter('all')}
              >
                All ({videosList.length})
              </button>
              <button
                type="button"
                className={`yt-studio-content-tab-btn ${contentFilter === 'videos' ? 'active' : ''}`}
                onClick={() => setContentFilter('videos')}
              >
                <MdOutlineVideocam size={17} />
                Videos ({videosList.filter((v) => !v.isShort).length})
              </button>
              <button
                type="button"
                className={`yt-studio-content-tab-btn ${contentFilter === 'shorts' ? 'active' : ''}`}
                onClick={() => setContentFilter('shorts')}
              >
                <StudioShortsIcon size={14} color={contentFilter === 'shorts' ? '#ff4e45' : '#aaaaaa'} />
                Shorts ({videosList.filter((v) => v.isShort).length})
              </button>
            </div>

            {/* Video & Shorts Table */}
            {(() => {
              const filteredVideos = videosList.filter((vid) => {
                if (contentFilter === 'shorts') return Boolean(vid.isShort);
                if (contentFilter === 'videos') return !vid.isShort;
                return true;
              });

              return (
                <div className="yt-studio-table-container">
                  <table className="yt-studio-table">
                    <thead>
                      <tr>
                        <th>{contentFilter === 'shorts' ? 'Short' : contentFilter === 'videos' ? 'Video' : 'Content'}</th>
                        <th>Visibility</th>
                        <th>Date</th>
                        <th>Views</th>
                        <th>Comments</th>
                        <th>Likes</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredVideos.length > 0 ? (
                        filteredVideos.map((vid) => (
                          <tr key={vid.id}>
                            <td>
                              <div className="yt-studio-table-video-cell">
                                <div className={`yt-studio-video-thumb ${vid.isShort ? 'is-short' : ''}`}>
                                  {vid.thumbnailUrl ? (
                                    <img
                                      src={vid.thumbnailUrl}
                                      alt={vid.title}
                                      className="yt-studio-thumb-img"
                                      onError={(e) => {
                                        e.currentTarget.style.display = 'none';
                                        if (e.currentTarget.nextElementSibling) {
                                          e.currentTarget.nextElementSibling.style.display = 'flex';
                                        }
                                      }}
                                    />
                                  ) : null}
                                  <div
                                    className="yt-studio-thumb-fallback"
                                    style={{
                                      display: vid.thumbnailUrl ? 'none' : 'flex',
                                    }}
                                  >
                                    {vid.isShort ? <StudioShortsIcon size={20} color="#ff4e45" /> : <MdOutlineVideocam size={24} />}
                                  </div>
                                  {vid.isShort && (
                                    <span className="yt-studio-short-thumb-badge" title="Speedo Short">
                                      <StudioShortsIcon size={12} color="#ffffff" />
                                    </span>
                                  )}
                                </div>
                                <div className="yt-studio-video-meta">
                                  <div className="yt-studio-video-title">
                                    {vid.isShort && (
                                      <span className="yt-studio-short-chip" title="Short video">
                                        <StudioShortsIcon size={11} color="#ff4e45" /> Shorts
                                      </span>
                                    )}
                                    {vid.title}
                                  </div>
                                  <div className="yt-studio-video-desc">{vid.description || 'No description'}</div>
                                </div>
                                <div className="yt-studio-video-spacer" aria-hidden="true" />
                              </div>
                            </td>
                            <td>
                              {vid.isDenied ? (
                                <div className="yt-studio-denied-badge-wrapper">
                                  <span className="yt-studio-vis-badge denied">
                                    <MdErrorOutline size={14} /> Denied
                                  </span>
                                  <div className="yt-studio-denied-tooltip">
                                    The {vid.isShort ? 'Shorts' : 'Video'} you have published does not meet SpeedoTube guidelines or norms. Please contact us for more support.
                                  </div>
                                </div>
                              ) : vid.isApproved ? (
                                <span className={`yt-studio-vis-badge ${vid.visibility?.toLowerCase() || 'public'}`}>
                                  {vid.visibility || 'Public'}
                                </span>
                              ) : (
                                <span
                                  className="yt-studio-vis-badge review-pending"
                                  data-yt-tooltip="Awaiting Admin Review & Approval in CRM"
                                >
                                  Review Pending
                                </span>
                              )}
                            </td>
                            <td>{new Date(vid.createdAt).toLocaleDateString()}</td>
                            <td>{vid.views || 0}</td>
                            <td>{vid.commentsCount || 0}</td>
                            <td>{vid.likes || 0}</td>
                            <td>
                              <div className="yt-studio-action-icons">
                                <button
                                  data-yt-tooltip={vid.isShort ? "View Short" : "View Video"}
                                  aria-label="View on SpeedoTube"
                                  onClick={() => {
                                    const targetId = vid.videoUrlId || vid.dbId || vid.id;
                                    const path = vid.isShort ? `/shorts/${targetId}` : `/watch?v=${targetId}`;
                                    if (onNavigate) onNavigate(path);
                                  }}
                                >
                                  <MdOutlinePlayArrow size={19} />
                                </button>
                                <button
                                  data-yt-tooltip="Edit details"
                                  aria-label="Edit Video Details"
                                  onClick={() => handleOpenEdit(vid)}
                                >
                                  <MdOutlineEdit size={18} />
                                </button>
                                {vid.isDenied && (
                                  <button
                                    className="yt-studio-action-delete-btn"
                                    data-yt-tooltip={vid.isShort ? "Remove denied short" : "Remove denied video"}
                                    data-tooltip-align="right"
                                    aria-label={vid.isShort ? "Remove Denied Short" : "Remove Denied Video"}
                                    onClick={() => handleDeleteVideo(vid.id)}
                                  >
                                    <MdOutlineDelete size={18} />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="7" style={{ textAlign: 'center', padding: '40px', color: '#aaa' }}>
                            {contentFilter === 'shorts'
                              ? 'No Shorts found. Upload a Short to see it here!'
                              : contentFilter === 'videos'
                              ? 'No videos found. Upload a video to see it here!'
                              : 'No content found. Upload a video or short to start managing your channel content!'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </div>
        )}

        {/* ==================== 2.5 MUSIC ALBUMS VIEW ==================== */}
        {activeTab === 'albums' && (
          <div className="yt-studio-view-container">
            <div className="yt-studio-header-row">
              <div>
                <h1 className="yt-studio-page-title">Music Albums</h1>
                <p className="yt-studio-page-subtitle">Manage your studio album releases, tracklists, visibility and metadata</p>
              </div>
            </div>

            {/* Albums Table */}
            <div className="yt-studio-table-container">
              <table className="yt-studio-table">
                <thead>
                  <tr>
                    <th>Album Banner</th>
                    <th>Visibility</th>
                    <th>Release Date</th>
                    <th>Genre</th>
                    <th>Tracks</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {albumsList.length > 0 ? (
                    albumsList.map((album) => {
                      const trackCount = Array.isArray(album.tracks) ? album.tracks.length : 0;
                      return (
                        <tr key={album.id}>
                          <td>
                            <div className="yt-studio-table-video-cell">
                              <div className="yt-studio-album-thumb">
                                {album.coverUrl ? (
                                  <img
                                    src={album.coverUrl}
                                    alt={album.title}
                                    className="yt-studio-thumb-img"
                                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '10px' }}
                                    onError={(e) => {
                                      e.currentTarget.style.display = 'none';
                                      if (e.currentTarget.nextElementSibling) {
                                        e.currentTarget.nextElementSibling.style.display = 'flex';
                                      }
                                    }}
                                  />
                                ) : null}
                                <div
                                  className="yt-studio-thumb-fallback"
                                  style={{
                                    display: album.coverUrl ? 'none' : 'flex',
                                    borderRadius: '10px',
                                  }}
                                >
                                  <MdOutlineAlbum size={28} />
                                </div>
                              </div>
                              <div className="yt-studio-video-meta">
                                <div className="yt-studio-video-title" style={{ fontSize: '15px' }}>{album.title}</div>
                                <div className="yt-studio-video-desc">{album.artist} • {album.releaseYear || new Date().getFullYear()}</div>
                              </div>
                            </div>
                          </td>
                          <td>
                            {album.isDenied ? (
                              <div className="yt-studio-denied-badge-wrapper">
                                <span className="yt-studio-vis-badge denied">
                                  <MdErrorOutline size={14} /> Denied
                                </span>
                                <div className="yt-studio-denied-tooltip">
                                  The Album you have published does not meet SpeedoTube guidelines or norms. Please contact us for more support.
                                </div>
                              </div>
                            ) : album.isApproved ? (
                              <span className={`yt-studio-vis-badge ${album.visibility?.toLowerCase() || 'public'}`}>
                                {album.visibility || 'Public'}
                              </span>
                            ) : (
                              <span
                                className="yt-studio-vis-badge review-pending"
                                data-yt-tooltip="Awaiting Admin Review & Approval in CRM"
                              >
                                Review Pending
                              </span>
                            )}
                          </td>
                          <td>{album.createdAt ? new Date(album.createdAt).toLocaleDateString() : '—'}</td>
                          <td>
                            <span className="yt-studio-genre-badge">{album.genre || 'Music'}</span>
                          </td>
                          <td>
                            <span style={{ fontWeight: 600 }}>{trackCount} {trackCount === 1 ? 'Track' : 'Tracks'}</span>
                          </td>
                          <td>
                            <div className="yt-studio-action-icons">
                              <button
                                data-yt-tooltip="Inspect tracklist"
                                aria-label="Inspect tracklist"
                                onClick={() => setSelectedStudioAlbum(album)}
                              >
                                <MdQueueMusic size={19} />
                              </button>
                              {album.isDenied && (
                                <button
                                  className="yt-studio-action-delete-btn"
                                  data-yt-tooltip="Remove denied album"
                                  data-tooltip-align="right"
                                  aria-label="Remove denied album"
                                  onClick={() => setDeletingAlbumId(album.id)}
                                >
                                  <MdOutlineDelete size={19} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="6" style={{ textAlign: 'center', padding: '48px', color: '#aaa' }}>
                        <div style={{ maxWidth: '380px', margin: '0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px' }}>
                          <MdOutlineAlbum size={48} style={{ color: '#555' }} />
                          <p style={{ margin: 0, fontSize: '15px', color: '#ccc' }}>No music albums released yet.</p>
                          <p style={{ margin: 0, fontSize: '13px', color: '#888' }}>Upload an album with full tracklist and cover artwork to release your music on SpeedoTube!</p>
                          <button
                            className="yt-studio-dark-pill-btn"
                            onClick={() => {
                              if (onNavigate) onNavigate('/studio/upload-album');
                            }}
                          >
                            Upload Album
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ==================== 3. ANALYTICS VIEW ==================== */}
        {activeTab === 'analytics' && (
          <div className="yt-studio-view-container">
            <div className="yt-studio-header-row">
              <div>
                <h1 className="yt-studio-page-title">Channel Analytics</h1>
                <p className="yt-studio-page-subtitle">Track your audience reach, engagement metrics, and channel growth</p>
              </div>
            </div>

            {/* Analytics Subtabs */}
            <div className="yt-studio-subtabs">
              {['overview', 'reach', 'engagement', 'audience'].map((tab) => (
                <button
                  key={tab}
                  className={`yt-studio-subtab ${analyticsTab === tab ? 'active' : ''}`}
                  onClick={() => setAnalyticsTab(tab)}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>

            {/* Metric Overview Cards / Selectable Tabs */}
            <div className="yt-studio-kpi-grid">
              <div
                className={`yt-studio-kpi-card selectable ${analyticsMetric === 'views' ? 'active views' : ''}`}
                onClick={() => setAnalyticsMetric('views')}
                title="Click to view Views trend"
              >
                <div className="yt-studio-kpi-label">Views</div>
                <div className="yt-studio-kpi-val">{totalChannelViews.toLocaleString()}</div>
                <div className="yt-studio-kpi-badge positive">
                  {totalChannelViews > 0 ? '+100% vs last 28 days' : '—'}
                </div>
              </div>

              <div
                className={`yt-studio-kpi-card selectable ${analyticsMetric === 'watchTime' ? 'active watch-time' : ''}`}
                onClick={() => {
                  setAnalyticsMetric('watchTime');
                  setShowWatchTimeModal(true);
                }}
                title="Click to view Watch Time breakdown by content"
              >
                <div className="yt-studio-kpi-label">Watch time (hours)</div>
                <div className="yt-studio-kpi-val">{(dashboardData?.summary28Days?.watchTimeHours || 0).toFixed(1)}</div>
                <div className="yt-studio-kpi-badge positive">
                  {(dashboardData?.summary28Days?.watchTimeHours || 0) > 0 ? '+100%' : '—'}
                </div>
              </div>

              <div
                className={`yt-studio-kpi-card selectable ${analyticsMetric === 'subscribers' ? 'active subscribers' : ''}`}
                onClick={() => setAnalyticsMetric('subscribers')}
                title="Click to view Subscribers trend"
              >
                <div className="yt-studio-kpi-label">Subscribers</div>
                <div className="yt-studio-kpi-val">{dashboardData?.subscribersCount || 0}</div>
                <div className="yt-studio-kpi-badge positive">
                  {(dashboardData?.subscribersCount || 0) > 0 ? '+100%' : '—'}
                </div>
              </div>
            </div>

            {/* Analytics Content Layout: Chart + Realtime Telemetry Card */}
            <div className="yt-studio-analytics-content-layout">
              {/* High-Fidelity Interactive Analytics Chart Card */}
              <div className="yt-studio-analytics-chart-card">
                {/* Header with Title and Range Dropdown */}
                <div className="yt-studio-chart-top-bar">
                  <div className="yt-studio-chart-title-area">
                    <h3 className="yt-studio-chart-main-heading">
                      {currentMetric.heading}
                    </h3>
                    <div className="yt-studio-chart-meta-row">
                      <span>{dateRangeString}</span>
                    </div>
                  </div>

                  <div className="yt-studio-chart-controls">
                    <div className="yt-studio-range-badge">
                      <MdOutlineSchedule /> Last 28 days
                    </div>
                  </div>
                </div>

                {/* Chart Canvas Area */}
                <div
                  className="yt-studio-chart-canvas-wrapper"
                  onMouseMove={handleChartMouseMove}
                  onMouseLeave={handleChartMouseLeave}
                  onTouchMove={handleChartMouseMove}
                >
                  <svg
                    ref={chartSvgRef}
                    viewBox="0 0 900 250"
                    className="yt-studio-svg-graph"
                    preserveAspectRatio="none"
                  >
                    <defs>
                      <linearGradient id="grad_views" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#3ea6ff" stopOpacity="0.32" />
                        <stop offset="70%" stopColor="#3ea6ff" stopOpacity="0.08" />
                        <stop offset="100%" stopColor="#3ea6ff" stopOpacity="0.0" />
                      </linearGradient>
                      <linearGradient id="grad_watchTime" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#2ba640" stopOpacity="0.32" />
                        <stop offset="70%" stopColor="#2ba640" stopOpacity="0.08" />
                        <stop offset="100%" stopColor="#2ba640" stopOpacity="0.0" />
                      </linearGradient>
                      <linearGradient id="grad_subscribers" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#a855f7" stopOpacity="0.32" />
                        <stop offset="70%" stopColor="#a855f7" stopOpacity="0.08" />
                        <stop offset="100%" stopColor="#a855f7" stopOpacity="0.0" />
                      </linearGradient>
                      <filter id="chartGlow" x="-20%" y="-20%" width="140%" height="140%">
                        <feDropShadow dx="0" dy="2" stdDeviation="4" floodColor={currentMetric.color} floodOpacity="0.35" />
                      </filter>
                    </defs>

                    {/* Horizontal Gridlines & Y-Axis Labels */}
                    {yTicks.map((tick, tIdx) => (
                      <g key={`ytick-${tIdx}`}>
                        <line
                          x1={chartLeft}
                          y1={tick.y}
                          x2={chartRight}
                          y2={tick.y}
                          className={tick.val === 0 ? 'yt-studio-chart-baseline' : 'yt-studio-chart-gridline'}
                          strokeDasharray={tick.val === 0 ? 'none' : '4 4'}
                          strokeWidth="1"
                        />
                        <text
                          x={chartLeft - 12}
                          y={tick.y + 4}
                          textAnchor="end"
                          className="yt-studio-chart-axis-label"
                        >
                          {tick.val}
                        </text>
                      </g>
                    ))}

                    {/* Baseline Axis */}
                    <line
                      x1={chartLeft}
                      y1={chartBottom}
                      x2={chartRight}
                      y2={chartBottom}
                      className="yt-studio-chart-baseline"
                      strokeWidth="1.2"
                    />

                    {/* X-Axis Date Ticks & Labels */}
                    {sampleDateIndices.map((idx) => {
                      const item = analyticsTimeline[idx];
                      if (!item) return null;
                      const xPos = chartLeft + (idx / 27) * chartWidth;
                      return (
                        <g key={`xtick-${idx}`}>
                          <line
                            x1={xPos}
                            y1={chartBottom}
                            x2={xPos}
                            y2={chartBottom + 5}
                            className="yt-studio-chart-tick"
                          />
                          <text
                            x={xPos}
                            y={chartBottom + 22}
                            textAnchor="middle"
                            className="yt-studio-chart-axis-label"
                          >
                            {item.dateStr}
                          </text>
                        </g>
                      );
                    })}

                    {/* Data Spline and Gradient Area */}
                    {currentMetric.value > 0 ? (
                      <>
                        {/* Gradient Fill Under Spline */}
                        <path
                          d={areaPath}
                          fill={`url(#${currentMetric.gradientId})`}
                        />
                        {/* Spline Curve Stroke */}
                        <path
                          d={splinePath}
                          fill="none"
                          stroke={currentMetric.color}
                          strokeWidth="2.75"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          filter="url(#chartGlow)"
                        />
                      </>
                    ) : (
                      /* Zero Activity Reference Baseline */
                      <line
                        x1={chartLeft}
                        y1={chartBottom}
                        x2={chartRight}
                        y2={chartBottom}
                        className="yt-studio-chart-zero-line"
                        strokeWidth="2"
                        strokeDasharray="6 6"
                      />
                    )}

                    {/* Interactive Scrubber Indicator on Hover */}
                    {hoveredPoint && (
                      <g className="yt-studio-chart-scrubber">
                        <line
                          x1={hoveredPoint.x}
                          y1={chartTop}
                          x2={hoveredPoint.x}
                          y2={chartBottom}
                          className="yt-studio-chart-scrubber-line"
                          strokeDasharray="3 3"
                          strokeWidth="1.2"
                        />
                        <circle
                          cx={hoveredPoint.x}
                          cy={hoveredPoint.y}
                          r="9"
                          fill={currentMetric.color}
                          fillOpacity="0.28"
                        />
                        <circle
                          cx={hoveredPoint.x}
                          cy={hoveredPoint.y}
                          r="4.5"
                          fill="#ffffff"
                          stroke={currentMetric.color}
                          strokeWidth="2.5"
                        />
                      </g>
                    )}
                  </svg>

                  {/* Floating YouTube Studio Interactive Tooltip */}
                  {hoveredPoint && (
                    <div
                      className="yt-studio-chart-tooltip"
                      style={{
                        left: `${(hoveredPoint.x / 900) * 100}%`,
                        top: `${Math.max(10, (hoveredPoint.y / 250) * 100 - 12)}%`,
                        transform:
                          hoveredPoint.x < 130
                            ? 'translate(0%, -100%)'
                            : hoveredPoint.x > 770
                            ? 'translate(-100%, -100%)'
                            : 'translate(-50%, -100%)',
                      }}
                    >
                      <div className="yt-studio-tooltip-date">{hoveredPoint.item.fullDateStr}</div>
                      <div className="yt-studio-tooltip-metric">
                        <span
                          className="yt-studio-tooltip-dot"
                          style={{ background: currentMetric.color }}
                        />
                        <span>
                          {currentMetric.label}: {hoveredPoint.val}{' '}
                          {currentMetric.key === 'subscribers'
                            ? (hoveredPoint.val === 1 ? 'subscriber' : 'subscribers')
                            : currentMetric.unit.trim()}
                        </span>
                      </div>
                      <div className="yt-studio-tooltip-sub">
                        {hoveredPoint.val > 0 ? '+100% vs typical' : 'No activity recorded'}
                      </div>
                    </div>
                  )}
                </div>

                {/* Card Footer with Live Status */}
                <div className="yt-studio-chart-card-footer">
                  <span className="yt-studio-chart-footer-note">Updated in real time &middot; First 24 hours of video release may differ</span>
                </div>
              </div>

              {/* YouTube Studio Realtime Live Telemetry Card */}
              <div className="yt-studio-realtime-card">
                <div className="yt-studio-realtime-header">
                  <div>
                    <h4 className="yt-studio-realtime-title">Realtime</h4>
                    <div className="yt-studio-realtime-updating">
                      <span className="yt-studio-live-dot" /> Updating live
                    </div>
                  </div>
                </div>

                <div className="yt-studio-realtime-metric-block">
                  <div className="yt-studio-realtime-big-num">
                    {dashboardData?.subscribersCount || 0}
                  </div>
                  <div className="yt-studio-realtime-metric-label">Subscribers</div>
                  <button
                    type="button"
                    className="yt-studio-see-live-sub-btn"
                    onClick={() => setShowLiveSubModal(true)}
                  >
                    <span>See Live Count</span>
                  </button>
                </div>

                <div className="yt-studio-realtime-divider" />

                <div className="yt-studio-realtime-metric-block">
                  <div className="yt-studio-realtime-big-num">
                    {totalChannelViews.toLocaleString()}
                  </div>
                  <div className="yt-studio-realtime-metric-label">Views &middot; Last 48 hours</div>
                </div>

                <div className="yt-studio-realtime-badge-tip">
                  Channel metrics sync live with backend database
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ==================== 4. COMMENTS VIEW ==================== */}
        {activeTab === 'comments' && (
          <div className="yt-studio-view-container">
            <div className="yt-studio-header-row">
              <div>
                <h1 className="yt-studio-page-title">Channel Comments</h1>
                <p className="yt-studio-page-subtitle">Moderate viewer comments and engage with your audience</p>
              </div>
            </div>

            {/* Moderation Filter Tabs */}
            <div className="yt-studio-subtabs">
              <button
                className={`yt-studio-subtab ${communityFilter === 'published' ? 'active' : ''}`}
                onClick={() => setCommunityFilter('published')}
              >
                Published
              </button>
              <button
                className={`yt-studio-subtab ${communityFilter === 'review' ? 'active' : ''}`}
                onClick={() => setCommunityFilter('review')}
              >
                Held for review
              </button>
            </div>

            {/* Comment List */}
            <div className="yt-studio-comments-list">
              {communityComments.length > 0 ? (
                communityComments.map((cmt) => (
                  <div key={cmt.id} className="yt-studio-comment-card">
                    <div
                      className="yt-studio-comment-avatar"
                      style={{ backgroundColor: getAvatarColor(cmt.author), color: '#ffffff' }}
                    >
                      {cmt.avatar}
                    </div>
                    <div className="yt-studio-comment-body">
                      <div className="yt-studio-comment-meta">
                        <span className="yt-studio-comment-author">{cmt.author}</span> &middot;{' '}
                        <span className="yt-studio-comment-time">{cmt.time}</span> on{' '}
                        <strong>{cmt.videoTitle}</strong>
                      </div>
                      <div className="yt-studio-comment-text">{cmt.text}</div>
                      <div className="yt-studio-comment-actions">
                        <span className="yt-studio-comment-counts" style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '13px', color: '#606060', marginRight: '10px' }}>
                          <MdThumbUp size={15} color={cmt.likeCount > 0 ? '#0f0f0f' : '#888'} />
                          <span>{cmt.likeCount || 0}</span>
                          {cmt.dislikeCount > 0 && (
                            <span style={{ marginLeft: '8px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                              <MdThumbDown size={15} color="#888" />
                              <span>{cmt.dislikeCount}</span>
                            </span>
                          )}
                        </span>
                        <button title="Heart Comment">
                          <MdOutlineFavorite size={18} color={cmt.hearts ? '#e91e63' : '#aaa'} />
                        </button>
                        <button title="Reply">
                          <MdOutlineChatBubbleOutline size={18} />
                        </button>
                        <button title="Remove Comment">
                          <MdOutlineDelete size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="yt-studio-empty-box">No comments found in this queue.</div>
              )}
            </div>
          </div>
        )}

        {/* ==================== 5. AUDIO LIBRARY VIEW ==================== */}
        {activeTab === 'audio' && (
          <div className="yt-studio-view-container">
            <div className="yt-studio-header-row">
              <div>
                <h1 className="yt-studio-page-title">Audio Library</h1>
                <p className="yt-studio-page-subtitle">Royalty-free production music for your Speedo Tube videos</p>
              </div>
            </div>

            {/* Audio Search & Filters */}
            <div className="yt-studio-audio-bar">
              <div className="yt-studio-audio-search">
                <MdOutlineSearch size={20} />
                <input
                  type="text"
                  placeholder="Search audio library tracks..."
                  value={audioSearch}
                  onChange={(e) => setAudioSearch(e.target.value)}
                />
              </div>
              <div className="yt-studio-genre-tags">
                {['all', 'synthwave', 'lo-fi', 'cinematic', 'groove', 'hip hop'].map((g) => (
                  <button
                    key={g}
                    className={`yt-studio-genre-tag ${audioGenre === g ? 'active' : ''}`}
                    onClick={() => setAudioGenre(g)}
                  >
                    {g.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>

            {/* Audio Tracks Table */}
            <div className="yt-studio-table-container">
              <table className="yt-studio-table">
                <thead>
                  <tr>
                    <th>Track Title</th>
                    <th>Artist</th>
                    <th>Genre</th>
                    <th>Duration</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {audioTracks.map((track) => (
                    <tr key={track.id}>
                      <td>
                        <strong>{track.title}</strong>
                      </td>
                      <td>{track.artist}</td>
                      <td>
                        <span className="yt-studio-genre-badge">{track.genre}</span>
                      </td>
                      <td>{track.duration}</td>
                      <td>
                        <div className="yt-studio-audio-actions">
                          <button
                            className="yt-studio-action-icon-btn"
                            onClick={() => togglePlayAudio(track)}
                            title={playingAudioId === track.id ? "Pause" : "Play"}
                          >
                            {playingAudioId === track.id ? (
                              <MdOutlinePause size={20} />
                            ) : (
                              <MdOutlinePlayArrow size={20} />
                            )}
                          </button>
                          <button
                            className="yt-studio-action-icon-btn"
                            onClick={() => handleDownloadAudio(track)}
                            title={`Download ${track.title}`}
                          >
                            <MdOutlineDownload size={20} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {/* ==================== 6. CHANNEL CUSTOMIZATION VIEW ==================== */}
        {activeTab === 'customization' && (
          <div className="yt-studio-view-container">
            {/* Floating Action Bar — fixed to bottom of viewport, YouTube Studio style */}
            <div className="yt-studio-floating-action-bar">
              <button
                type="button"
                className="yt-studio-fab-btn yt-studio-fab-btn--cancel"
                disabled={!isCustomizationDirty || customizationSaving}
                onClick={handleCancelCustomization}
              >
                Cancel
              </button>

              <button
                type="button"
                className="yt-studio-fab-btn yt-studio-fab-btn--save"
                disabled={!isCustomizationDirty || customizationSaving}
                onClick={() => handleSaveCustomization()}
              >
                {customizationSaving ? (
                  <>
                    <span className="spinner-border spinner-border-sm" role="status" style={{ width: '13px', height: '13px', borderWidth: '2px' }} />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>

            <div className="yt-studio-header-row">
              <div>
                <h1 className="yt-studio-page-title">Channel customization</h1>
                <p className="yt-studio-page-subtitle">Customize your channel's layout, branding, and basic info</p>
              </div>
            </div>

            {/* Customization Sub-Tabs Header */}
            <div className="yt-studio-subtabs" style={{ marginBottom: '24px' }}>
              <button
                className={`yt-studio-subtab ${customizationTab === 'profile' || customizationTab === 'branding' || customizationTab === 'basic' ? 'active' : ''}`}
                onClick={() => setCustomizationTab('profile')}
              >
                Profile & Branding
              </button>
              <button
                className={`yt-studio-subtab ${customizationTab === 'layout' ? 'active' : ''}`}
                onClick={() => setCustomizationTab('layout')}
              >
                Home tab (Featuring)
              </button>
            </div>

            {/* Hidden File Inputs for Branding Assets */}
            <input
              type="file"
              ref={bannerFileRef}
              accept="image/*"
              style={{ display: 'none' }}
              onChange={(e) => handleImageFileChange(e, 'banner')}
            />
            <input
              type="file"
              ref={avatarFileRef}
              accept="image/*"
              style={{ display: 'none' }}
              onChange={(e) => handleImageFileChange(e, 'avatar')}
            />
            <input
              type="file"
              ref={watermarkFileRef}
              accept="image/*"
              style={{ display: 'none' }}
              onChange={(e) => handleImageFileChange(e, 'watermark')}
            />

            {/* TAB 1: PROFILE (BANNER, PICTURE, NAME, HANDLE, DESC, URL, LINKS, CONTACT, WATERMARK) */}
            {(customizationTab === 'profile' || customizationTab === 'branding' || customizationTab === 'basic') && (
              <div className="yt-studio-customization-section">
                {/* 1. Banner Image */}
                <div className="yt-studio-custom-card">
                  <h3 className="yt-studio-custom-card-title">Banner image</h3>
                  <p className="yt-studio-custom-card-subtitle">
                    This image will appear across the top of your channel
                  </p>
                  <div className="yt-studio-branding-row">
                    <div className="spt-banner-mockup-box">
                      {bannerPreview ? (
                        <img src={bannerPreview} alt="Banner Preview" className="spt-banner-img" />
                      ) : (
                        <svg width="180" height="100" viewBox="0 0 200 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect x="25" y="10" width="150" height="85" rx="4" fill="#E2E8F0" stroke="#94A3B8" strokeWidth="2" />
                          <path d="M26 11H174V40H26V11Z" fill="#CC0000" />
                          <rect x="26" y="40" width="148" height="54" fill="#FFFFFF" />
                          <rect x="85" y="95" width="30" height="14" fill="#94A3B8" />
                          <rect x="70" y="109" width="60" height="4" rx="2" fill="#64748B" />

                          <rect x="15" y="45" width="85" height="52" rx="3" fill="#1E293B" />
                          <path d="M16 46H99V64H16V46Z" fill="#CC0000" />
                          <rect x="16" y="64" width="83" height="32" fill="#F8FAFC" />
                          <path d="M10 97H105L100 102H15L10 97Z" fill="#475569" />

                          <rect x="120" y="58" width="26" height="42" rx="3" fill="#0F172A" stroke="#334155" strokeWidth="1.5" />
                          <rect x="122" y="60" width="22" height="38" fill="#FFFFFF" />
                          <rect x="122" y="60" width="22" height="14" fill="#CC0000" />
                        </svg>
                      )}
                    </div>
                    <div>
                      <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: '#aaa', maxWidth: '420px', lineHeight: '1.5' }}>
                        For the best results on all devices, use an image that's at least 2048 x 1152 pixels and 8MB or less.
                      </p>
                      <div className="yt-studio-branding-actions">
                        <button className="yt-studio-white-pill-btn" onClick={() => bannerFileRef.current?.click()}>
                          {bannerPreview ? 'Change' : 'Upload'}
                        </button>
                        {bannerPreview && (
                          <button
                            className="yt-studio-dark-pill-btn"
                            onClick={() => handleRemoveAsset('banner')}
                            style={{ marginLeft: '8px' }}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* 2. Picture */}
                <div className="yt-studio-custom-card">
                  <h3 className="yt-studio-custom-card-title">Picture</h3>
                  <p className="yt-studio-custom-card-subtitle">
                    Your profile picture will appear where your channel is presented on SpeedoTube, like next to your videos and comments
                  </p>
                  <div className="yt-studio-branding-row">
                    <div className="spt-picture-mockup-box">
                      {avatarPreview ? (
                        <img src={avatarPreview} alt="Profile Avatar" className="spt-picture-img" />
                      ) : (
                        <div className="spt-picture-circle" style={{ backgroundColor: avatarColor }}>
                          {profileInitial}
                        </div>
                      )}
                    </div>
                    <div>
                      <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: '#aaa', maxWidth: '420px', lineHeight: '1.5' }}>
                        It's recommended to use a picture that's at least 98 x 98 pixels and 8MB or less. Use a PNG, JPG, or GIF file.
                      </p>
                      <div className="yt-studio-branding-actions">
                        <button className="yt-studio-white-pill-btn" onClick={() => avatarFileRef.current?.click()}>
                          {avatarPreview ? 'Change' : 'Upload'}
                        </button>
                        {avatarPreview && (
                          <button
                            className="yt-studio-dark-pill-btn"
                            onClick={() => handleRemoveAsset('avatar')}
                            style={{ marginLeft: '8px' }}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* 3. Name */}
                <div className="yt-studio-custom-card">
                  <h3 className="yt-studio-custom-card-title">Name</h3>
                  <p className="yt-studio-custom-card-subtitle">
                    Choose a channel name that represents you and your content. Changes made to your name and picture are visible only on SpeedoTube.
                  </p>
                  <div className="yt-studio-field" style={{ maxWidth: '600px' }}>
                    <input
                      type="text"
                      className="yt-studio-input"
                      value={customChannelName}
                      onChange={(e) => setCustomChannelName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleSaveCustomization();
                        }
                      }}
                      placeholder="Enter your channel name"
                      autoComplete="off"
                      spellCheck="false"
                    />
                  </div>
                </div>

                {/* 4. Handle */}
                <div className="yt-studio-custom-card">
                  <h3 className="yt-studio-custom-card-title">Handle</h3>
                  <p className="yt-studio-custom-card-subtitle">
                    Your handle is a unique @name that helps people find your channel, different from your channel name.
                  </p>
                  <div className="yt-studio-field" style={{ maxWidth: '600px', marginBottom: '8px' }}>
                    <input
                      type="text"
                      className="yt-studio-input"
                      value={customChannelHandle}
                      onChange={(e) => setCustomChannelHandle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleSaveCustomization();
                        }
                      }}
                      placeholder="@yourhandle"
                      autoComplete="off"
                      spellCheck="false"
                    />
                  </div>
                  <span style={{ fontSize: '13px', color: '#888888' }}>
                    https://speedotube.com/{customChannelHandle.startsWith('@') ? customChannelHandle : `@${customChannelHandle}`}
                  </span>
                </div>

                {/* 5. Description */}
                <div className="yt-studio-custom-card">
                  <h3 className="yt-studio-custom-card-title">Description</h3>
                  <p className="yt-studio-custom-card-subtitle">
                    Tell viewers about your channel. Your description will appear in the About section of your channel and search results, among other places.
                  </p>
                  <textarea
                    className="yt-studio-textarea"
                    rows={4}
                    placeholder="Tell viewers about your channel..."
                    value={customChannelDesc}
                    onChange={(e) => setCustomChannelDesc(e.target.value)}
                    style={{ maxWidth: '600px', marginBottom: '16px' }}
                  />
                </div>

                {/* 6. Channel URL */}
                <div className="yt-studio-custom-card">
                  <h3 className="yt-studio-custom-card-title">Channel URL</h3>
                  <p className="yt-studio-custom-card-subtitle">
                    This is the standard web address for your channel. It includes your unique handle or channel identifier.
                  </p>
                  <div className="yt-studio-audio-search" style={{ maxWidth: '600px', cursor: 'pointer' }}>
                    <input
                      type="text"
                      readOnly
                      value={`https://speedotube.com/${customChannelHandle.startsWith('@') ? customChannelHandle : `@${customChannelHandle}`}`}
                    />
                    <button
                      className="yt-studio-action-icon-btn"
                      onClick={() => {
                        const url = `https://speedotube.com/${customChannelHandle.startsWith('@') ? customChannelHandle : `@${customChannelHandle}`}`;
                        if (navigator.clipboard) {
                          navigator.clipboard.writeText(url);
                          setUrlCopied(true);
                          setTimeout(() => setUrlCopied(false), 2000);
                        }
                      }}
                      title="Copy channel URL"
                    >
                      <MdOutlineContentCopy size={18} />
                    </button>
                    {urlCopied && <span style={{ color: '#4caf50', fontSize: '12px', fontWeight: 600, marginLeft: '8px' }}>Copied!</span>}
                  </div>
                </div>

                {/* 7. Links */}
                <div className="yt-studio-custom-card">
                  <h3 className="yt-studio-custom-card-title">Links</h3>
                  <p className="yt-studio-custom-card-subtitle">
                    Share external links with your viewers. They'll be visible on your channel profile and about page.
                  </p>

                  {customLinks && customLinks.length > 0 && (
                    <div style={{ marginBottom: '16px' }}>
                      {customLinks.map((link, idx) => (
                        <div className="yt-studio-custom-link-row" key={link.id || idx}>
                          <div className="yt-studio-custom-link-info">
                            <strong>{link.title}</strong>
                            <a href={link.url} target="_blank" rel="noopener noreferrer">
                              {link.url}
                            </a>
                          </div>
                          <button
                            className="yt-studio-action-icon-btn"
                            onClick={() => handleRemoveLink(idx)}
                            title="Remove link"
                          >
                            <MdOutlineDelete size={18} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {isAddingLink ? (
                    <div style={{ maxWidth: '500px', padding: '16px', background: 'rgba(255,255,255,0.03)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.08)' }}>
                      <div className="yt-studio-field" style={{ marginBottom: '10px' }}>
                        <label style={{ fontSize: '12px', color: '#aaa', display: 'block', marginBottom: '4px' }}>Link Title</label>
                        <input
                          type="text"
                          className="yt-studio-input"
                          placeholder="e.g. Official Website, Twitter, Discord"
                          value={newLinkTitle}
                          onChange={(e) => setNewLinkTitle(e.target.value)}
                        />
                      </div>
                      <div className="yt-studio-field" style={{ marginBottom: '14px' }}>
                        <label style={{ fontSize: '12px', color: '#aaa', display: 'block', marginBottom: '4px' }}>URL</label>
                        <input
                          type="text"
                          className="yt-studio-input"
                          placeholder="e.g. https://example.com"
                          value={newLinkUrl}
                          onChange={(e) => setNewLinkUrl(e.target.value)}
                        />
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button className="yt-studio-white-pill-btn" onClick={handleAddLinkSubmit}>
                          Add Link
                        </button>
                        <button
                          className="yt-studio-dark-pill-btn"
                          onClick={() => {
                            setIsAddingLink(false);
                            setNewLinkTitle('');
                            setNewLinkUrl('');
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button className="yt-studio-dark-pill-btn" onClick={() => setIsAddingLink(true)}>
                      + Add link
                    </button>
                  )}
                </div>

                {/* 8. Contact info */}
                <div className="yt-studio-custom-card">
                  <h3 className="yt-studio-custom-card-title">Contact info</h3>
                  <p className="yt-studio-custom-card-subtitle">
                    Let people know how to contact you with business inquiries. The email address you enter may appear in the About section of your channel.
                  </p>
                  <div className="yt-studio-field" style={{ maxWidth: '350px' }}>
                    <input
                      type="email"
                      className="yt-studio-input"
                      placeholder="Email address"
                      value={channelEmail}
                      onChange={(e) => setChannelEmail(e.target.value)}
                    />
                  </div>
                </div>

                {/* 9. Video watermark */}
                <div className="yt-studio-custom-card">
                  <h3 className="yt-studio-custom-card-title">Video watermark</h3>
                  <p className="yt-studio-custom-card-subtitle">
                    The watermark will appear on your videos in the right-hand corner of the video player
                  </p>
                  <div className="yt-studio-branding-row">
                    <div className="spt-watermark-mockup-box">
                      <div className="spt-player-screen">
                        <div className="spt-watermark-badge">
                          {watermarkPreview ? (
                            <img src={watermarkPreview} alt="Watermark" className="spt-watermark-img" />
                          ) : (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                              <rect x="2" y="2" width="9" height="9" fill="#CC0000" />
                              <rect x="13" y="2" width="9" height="9" fill="#CC0000" opacity="0.6" />
                              <rect x="2" y="13" width="9" height="9" fill="#CC0000" opacity="0.6" />
                              <rect x="13" y="13" width="9" height="9" fill="#CC0000" />
                            </svg>
                          )}
                        </div>
                        <div className="spt-player-bar">
                          <span>▶ 🔊</span>
                          <span>⚙ 🗖 ⛶</span>
                        </div>
                      </div>
                    </div>
                    <div>
                      <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: '#aaa', maxWidth: '420px', lineHeight: '1.5' }}>
                        An image that's 150 x 150 pixels is recommended. Use a PNG, GIF, or JPEG file that's 2MB or less.
                      </p>
                      <div className="yt-studio-branding-actions">
                        <button className="yt-studio-white-pill-btn" onClick={() => watermarkFileRef.current?.click()}>
                          {watermarkPreview ? 'Change' : 'Upload'}
                        </button>
                        {watermarkPreview && (
                          <button
                            className="yt-studio-dark-pill-btn"
                            onClick={() => handleRemoveAsset('watermark')}
                            style={{ marginLeft: '8px' }}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* TAB 2: HOME TAB (LAYOUT) */}
            {customizationTab === 'layout' && (
              <div className="yt-studio-customization-section">
                <div className="yt-studio-custom-card">
                  <h3 className="yt-studio-custom-card-title">Video spotlight</h3>
                  <p className="yt-studio-custom-card-subtitle">
                    Add a video to the top of your channel homepage for specific audience segments
                  </p>

                  {/* Channel trailer for unsubscribed viewers */}
                  <div className="yt-studio-custom-spotlight-item" style={{ marginBottom: '20px', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <strong>Channel trailer for people who haven't subscribed</strong>
                      <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#aaaaaa' }}>
                        Share a preview of your channel with people who haven't subscribed yet.
                      </p>
                      {trailerVideo && (
                        <div className="yt-studio-spotlight-preview-box">
                          <img
                            src={trailerVideo.thumbnail || 'https://picsum.photos/seed/trailer/160/90'}
                            alt={trailerVideo.title}
                            className="yt-studio-spotlight-thumb"
                          />
                          <div className="yt-studio-spotlight-info">
                            <span className="yt-studio-spotlight-title">{trailerVideo.title}</span>
                            <span className="yt-studio-spotlight-meta">
                              {trailerVideo.views || '0'} views • {trailerVideo.duration || 'Video'}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button className="yt-studio-dark-pill-btn" onClick={() => openVideoSelectModal('trailer')}>
                        {trailerVideo ? 'Change' : '+ Add'}
                      </button>
                      {trailerVideo && (
                        <button
                          className="yt-studio-dark-pill-btn"
                          onClick={() => {
                            setTrailerVideo(null);
                            handleSaveCustomization({ trailerVideo: null });
                            showToast('Channel trailer removed', MdCheckCircle);
                          }}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Featured video for returning subscribers */}
                  <div className="yt-studio-custom-spotlight-item" style={{ alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <strong>Featured video for returning subscribers</strong>
                      <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#aaaaaa' }}>
                        Highlight a video for your subscribers to watch. This video won't be shown again at the top of your page for subscribers who have already watched it.
                      </p>
                      {featuredVideo && (
                        <div className="yt-studio-spotlight-preview-box">
                          <img
                            src={featuredVideo.thumbnail || 'https://picsum.photos/seed/featured/160/90'}
                            alt={featuredVideo.title}
                            className="yt-studio-spotlight-thumb"
                          />
                          <div className="yt-studio-spotlight-info">
                            <span className="yt-studio-spotlight-title">{featuredVideo.title}</span>
                            <span className="yt-studio-spotlight-meta">
                              {featuredVideo.views || '0'} views • {featuredVideo.duration || 'Video'}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button className="yt-studio-dark-pill-btn" onClick={() => openVideoSelectModal('featured')}>
                        {featuredVideo ? 'Change' : '+ Add'}
                      </button>
                      {featuredVideo && (
                        <button
                          className="yt-studio-dark-pill-btn"
                          onClick={() => {
                            setFeaturedVideo(null);
                            handleSaveCustomization({ featuredVideo: null });
                            showToast('Featured video removed', MdCheckCircle);
                          }}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="yt-studio-custom-card">
                  <h3 className="yt-studio-custom-card-title">Featured sections</h3>
                  <p className="yt-studio-custom-card-subtitle">
                    Customize the layout of your channel homepage with featured sections
                  </p>
                  {homeSections.map((section, idx) => (
                    <div
                      className="yt-studio-custom-spotlight-item"
                      key={section.id || idx}
                      style={{ marginBottom: idx < homeSections.length - 1 ? '16px' : '0' }}
                    >
                      <div>
                        <strong>{section.title}</strong>
                        <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#aaaaaa' }}>
                          {section.subtitle}
                        </p>
                      </div>
                      <button
                        className={`yt-studio-dark-pill-btn ${section.enabled ? 'active-section-btn' : ''}`}
                        onClick={() => toggleSection(section.id)}
                      >
                        {section.enabled ? 'Visible on Home' : 'Hidden'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Video Selector Modal for Spotlight (Trailer & Featured Video) */}
        {videoSelectorModal && (
          <div className="yt-studio-modal-overlay" onClick={() => setVideoSelectorModal(null)}>
            <div className="yt-studio-modal" style={{ maxWidth: '680px', width: '90%' }} onClick={(e) => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h2 style={{ margin: 0 }}>
                  Choose a video for {videoSelectorModal === 'trailer' ? 'Channel Trailer' : 'Featured Video'}
                </h2>
                <button
                  className="yt-studio-action-icon-btn"
                  onClick={() => setVideoSelectorModal(null)}
                  title="Close"
                >
                  <MdClose size={22} />
                </button>
              </div>

              <div className="yt-studio-video-picker-grid">
                {videosList && videosList.length > 0 ? (
                  videosList.map((vid) => (
                    <div
                      key={vid.id}
                      className="yt-studio-video-picker-item"
                      onClick={() => handleSelectSpotlightVideo(vid)}
                    >
                      <img
                        src={vid.thumbnailUrl || vid.thumbnail || 'https://picsum.photos/seed/' + vid.id + '/240/135'}
                        alt={vid.title}
                        className="yt-studio-video-picker-thumb"
                      />
                      <div className="yt-studio-video-picker-details">
                        <span className="yt-studio-video-picker-title">{vid.title}</span>
                        <span className="yt-studio-video-picker-meta">
                          {vid.views || '0'} views • {vid.duration || 'Video'}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={{ gridColumn: '1 / -1', padding: '32px 16px', textAlign: 'center', color: '#888' }}>
                    <p style={{ margin: '0 0 12px 0' }}>No published videos found in your channel yet.</p>
                    <button
                      className="yt-studio-white-pill-btn"
                      onClick={() => {
                        handleSelectSpotlightVideo({
                          id: 1,
                          title: 'Welcome to My Channel (Preview Trailer)',
                          thumbnailUrl: 'https://picsum.photos/seed/trailer_preview/320/180',
                          views: '1',
                          duration: '1:45',
                        });
                      }}
                    >
                      Use Sample Channel Video
                    </button>
                  </div>
                )}
              </div>

              <div className="yt-studio-modal-actions" style={{ marginTop: '20px' }}>
                <button className="yt-studio-dark-pill-btn" onClick={() => setVideoSelectorModal(null)}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ==================== 8. SPEEDOEARN VIEW ==================== */}
        {activeTab === 'speedoearn' && (
          <div className="yt-studio-view-container yt-studio-speedoearn-view">
            {/* Hero Header */}
            <div className="yt-studio-earn-hero">
              <div className="yt-studio-earn-hero-badge">
                <span className="yt-studio-earn-pulse-dot" />
                <span>Speedoearn Creator Program</span>
              </div>
              <h1 className="yt-studio-earn-hero-title">
                Speedoearn <span className="yt-studio-earn-gradient-text">Creator Monetization</span>
              </h1>
              <p className="yt-studio-earn-hero-subtitle">
                Turn your creative passion into sustainable income. Speedoearn introduces automated video ad revenue, music album royalties, fan memberships, and instant viewer tips directly to your Speedonet account.
              </p>
              <div className="yt-studio-earn-hero-actions">
                <button
                  className={`yt-studio-earn-notify-btn ${speedoearnNotified ? 'notified' : ''}`}
                  onClick={() => {
                    setSpeedoearnNotified(true);
                    showToast("You're on the priority list! We'll notify you when Speedoearn goes live.", <SpeedoVerifiedBadge size={20} />);
                  }}
                >
                  {speedoearnNotified ? (
                    <>
                      <SpeedoVerifiedBadge size={18} />
                      <span>Priority Access Alert Active</span>
                    </>
                  ) : (
                    <>
                      <MdMonetizationOn size={18} />
                      <span>Notify Me When Speedoearn Launches</span>
                    </>
                  )}
                </button>
                <div className="yt-studio-earn-status-tag">
                  Status: <strong>Coming Soon (Unpublished)</strong>
                </div>
              </div>
            </div>

            {/* Speedoearn Blue Verification Tick Showcase Banner (Positioned Below Hero Card) */}
            <div className="yt-studio-earn-status-banner partner-pending">
              <div className="yt-studio-earn-status-left">
                <div className="yt-studio-earn-status-badge-wrap">
                  <SpeedoVerifiedBadge size={46} title="Official Blue Verification Tick" />
                </div>
                <div className="yt-studio-earn-status-details">
                  <div className="yt-studio-earn-status-heading">
                    <h3>Official Blue Verification Tick</h3>
                    <span className="yt-studio-earn-pending-pill">
                      <SpeedoVerifiedBadge size={13} /> Unlocks on Channel Monetization
                    </span>
                  </div>
                  <p className="yt-studio-earn-status-text">
                    SpeedoTube will award the official Blue Rosette Verification Tick exclusively to creators once channel monetization launches and their account qualifies for Speedoearn. Currently, all active channels display the standard verification mark.
                  </p>
                </div>
              </div>
              <div className="yt-studio-earn-status-actions">
                <div className="yt-studio-earn-preview-chip">
                  <span className="yt-studio-earn-preview-tag">
                    <SpeedoVerifiedBadge size={14} /> Upcoming Feature
                  </span>
                  <span className="yt-studio-earn-preview-sub">Speedoearn Exclusive</span>
                </div>
              </div>
            </div>

            {/* Program Highlights Grid (Four Cards in Line) */}
            <div className="yt-studio-earn-grid">
              <div className="yt-studio-earn-card">
                <div className="yt-studio-earn-card-icon glow-amber">
                  <MdOutlineVideocam size={26} />
                </div>
                <h3 className="yt-studio-earn-card-title">Watch Page & Shorts Ads</h3>
                <p className="yt-studio-earn-card-desc">
                  Earn competitive revenue shares from pre-roll, mid-roll, and banner ads displayed on your public videos and high-definition streams.
                </p>
                <div className="yt-studio-earn-card-pill">Up to 70% Creator Share</div>
              </div>

              <div className="yt-studio-earn-card">
                <div className="yt-studio-earn-card-icon glow-rose">
                  <MdOutlineAlbum size={26} />
                </div>
                <h3 className="yt-studio-earn-card-title">Music Album Royalties</h3>
                <p className="yt-studio-earn-card-desc">
                  Monetize every individual track and complete album published through SpeedoTube Studio with automated per-stream payouts.
                </p>
                <div className="yt-studio-earn-card-pill">Automated Track Licensing</div>
              </div>

              <div className="yt-studio-earn-card">
                <div className="yt-studio-earn-card-icon glow-blue">
                  <MdOutlineFavorite size={26} />
                </div>
                <h3 className="yt-studio-earn-card-title">Fan Memberships & Tiers</h3>
                <p className="yt-studio-earn-card-desc">
                  Provide exclusive member badges, early-access videos, and custom loyalty perks to your dedicated channel supporters.
                </p>
                <div className="yt-studio-earn-card-pill">Monthly Recurring Income</div>
              </div>

              <div className="yt-studio-earn-card">
                <div className="yt-studio-earn-card-icon glow-green">
                  <MdOutlineThumbUp size={26} />
                </div>
                <h3 className="yt-studio-earn-card-title">Super Thanks & Direct Tips</h3>
                <p className="yt-studio-earn-card-desc">
                  Enable viewers to tip you directly in video comments with colorful highlighted comments that stand out from the crowd.
                </p>
                <div className="yt-studio-earn-card-pill">Instant Direct Tipping</div>
              </div>
            </div>

            {/* Creator Readiness / Eligibility Preview */}
            <div className="yt-studio-earn-eligibility-card">
              <div className="yt-studio-earn-eligibility-header">
                <div>
                  <h3 className="yt-studio-earn-eligibility-title">Your Readiness Status</h3>
                  <p className="yt-studio-earn-eligibility-desc">
                    Track your channel milestone progress while we complete the final rollout.
                  </p>
                </div>
                <span className="yt-studio-earn-ready-badge">
                  In Progress
                </span>
              </div>

              <div className="yt-studio-earn-trackers">
                <div className="yt-studio-earn-tracker-box">
                  <div className="yt-studio-earn-tracker-meta">
                    <span>Subscribers</span>
                    <strong>{dashboardData?.subscribersCount || 0} / 1,000</strong>
                  </div>
                  <div className="yt-studio-earn-progress-track">
                    <div
                      className="yt-studio-earn-progress-bar"
                      style={{
                        width: `${Math.min(100, Math.max(8, ((dashboardData?.subscribersCount || 0) / 1000) * 100))}%`,
                      }}
                    />
                  </div>
                </div>

                <div className="yt-studio-earn-tracker-box">
                  <div className="yt-studio-earn-tracker-meta">
                    <span>Public Watch Hours</span>
                    <strong>{(dashboardData?.summary28Days?.watchTimeHours || 0).toFixed(1)} / 4,000 hrs</strong>
                  </div>
                  <div className="yt-studio-earn-progress-track">
                    <div
                      className="yt-studio-earn-progress-bar"
                      style={{
                        width: `${Math.min(100, Math.max(6, (((dashboardData?.summary28Days?.watchTimeHours || 0) / 4000) * 100)))}%`,
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Milestone Reward Box */}
              <div className="yt-studio-earn-reward-box">
                <div className="yt-studio-earn-reward-left">
                  <div className="yt-studio-earn-reward-badge-icon">
                    <SpeedoVerifiedBadge size={36} />
                  </div>
                  <div>
                    <div className="yt-studio-earn-reward-title">Monetization Milestone Reward</div>
                    <div className="yt-studio-earn-reward-sub">
                      Official Blue Verification Tick & Full Creator Partner Access
                    </div>
                  </div>
                </div>
                <div className="yt-studio-earn-reward-right">
                  <span className="yt-studio-earn-reward-tag pending">
                    Unlocks with Channel Monetization
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* --- Production-Ready SpeedoTube Video / Shorts Edit Modal --- */}
      {editingVideo && (
        <SpeedoTubeUploadModal
          isOpen={Boolean(editingVideo)}
          editingItem={editingVideo}
          onClose={() => setEditingVideo(null)}
          onPublishSuccess={async (updatedData) => {
            const patchData = {
              title: updatedData.title,
              description: updatedData.description,
              visibility: updatedData.visibility,
              thumbnail_url: updatedData.thumbnailUrl,
              tags: updatedData.tags,
              category: updatedData.category,
            };
            const targetId = editingVideo.dbId || editingVideo.id;
            await updateVideoDetails(targetId, patchData, channelId, profileHandle);
            setEditingVideo(null);
            loadTabContent(activeTab);
          }}
        />
      )}

      {/* Smooth Pop-up Confirm Modal for Video/Short Deletion */}
      {(() => {
        const targetVideo = videosList.find((v) => v.id === deletingVideoId || v.dbId === deletingVideoId);
        const isTargetDenied = Boolean(targetVideo?.isDenied);
        const itemType = targetVideo?.isShort ? 'short' : 'video';
        return (
          <ConfirmModal
            isOpen={Boolean(deletingVideoId)}
            title={isTargetDenied ? `Remove denied ${itemType}?` : `Delete ${itemType}?`}
            message={
              isTargetDenied
                ? `Are you sure you want to remove this denied ${itemType} from your channel content? This action cannot be undone.`
                : `Are you sure you want to permanently delete this ${itemType}? This action cannot be undone.`
            }
            confirmText={isTargetDenied ? "Remove permanently" : "Delete permanently"}
            cancelText="Cancel"
            icon={MdOutlineDelete}
            danger={true}
            onConfirm={handleConfirmDeleteVideo}
            onClose={() => setDeletingVideoId(null)}
          />
        );
      })()}

      {/* --- Album Tracklist Inspection Modal --- */}
      {selectedStudioAlbum && (
        <div className="yt-studio-tracklist-modal-backdrop" onClick={() => setSelectedStudioAlbum(null)}>
          <div className="yt-studio-tracklist-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="yt-studio-tracklist-modal-header">
              <div className="yt-studio-tracklist-header-info">
                {selectedStudioAlbum.coverUrl ? (
                  <img
                    src={selectedStudioAlbum.coverUrl}
                    alt={selectedStudioAlbum.title}
                    className="yt-studio-modal-cover-img"
                  />
                ) : (
                  <div className="yt-studio-modal-cover-fallback">
                    <MdOutlineAlbum size={36} />
                  </div>
                )}
                <div>
                  <h3 className="yt-studio-modal-album-title">{selectedStudioAlbum.title}</h3>
                  <div className="yt-studio-modal-album-meta">
                    <span>{selectedStudioAlbum.artist}</span>
                    <span>•</span>
                    <span>{selectedStudioAlbum.genre || 'Music'}</span>
                    <span>•</span>
                    <span>{selectedStudioAlbum.releaseYear || new Date().getFullYear()}</span>
                    {selectedStudioAlbum.recordLabel && (
                      <>
                        <span>•</span>
                        <span>{selectedStudioAlbum.recordLabel}</span>
                      </>
                    )}
                  </div>
                  <div style={{ marginTop: '6px', fontSize: '12px', color: '#888' }}>
                    {selectedStudioAlbum.tracks?.length || 0} Tracks Total
                  </div>
                </div>
              </div>
              <button
                className="yt-studio-action-icon-btn"
                onClick={() => setSelectedStudioAlbum(null)}
                title="Close"
              >
                <MdClose size={22} />
              </button>
            </div>

            <div className="yt-studio-tracklist-modal-body">
              <table className="yt-studio-table">
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}>#</th>
                    <th>Track Title</th>
                    <th>Featured Artist</th>
                    <th>Duration</th>
                    <th style={{ textAlign: 'right' }}>Audio</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedStudioAlbum.tracks && selectedStudioAlbum.tracks.length > 0 ? (
                    selectedStudioAlbum.tracks.map((track, idx) => (
                      <tr key={track.id || idx}>
                        <td>{track.trackNumber || idx + 1}</td>
                        <td>
                          <strong>{track.title}</strong>
                        </td>
                        <td>{track.artist || '—'}</td>
                        <td>{track.duration || '—'}</td>
                        <td>
                          <div className="yt-studio-audio-actions" style={{ justifyContent: 'flex-end' }}>
                            <button
                              className="yt-studio-action-icon-btn"
                              onClick={() => togglePlayAudio(track)}
                              data-yt-tooltip={playingAudioId === track.id ? "Pause" : "Play preview"}
                              aria-label={playingAudioId === track.id ? "Pause" : "Play preview"}
                            >
                              {playingAudioId === track.id ? (
                                <MdOutlinePause size={18} />
                              ) : (
                                <MdOutlinePlayArrow size={18} />
                              )}
                            </button>
                            {track.url && (
                              <button
                                className="yt-studio-action-icon-btn"
                                onClick={() => handleDownloadAudio(track)}
                                data-yt-tooltip={`Download ${track.title}`}
                                data-tooltip-align="right"
                                aria-label={`Download ${track.title}`}
                              >
                                <MdOutlineDownload size={18} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" style={{ textAlign: 'center', padding: '24px', color: '#888' }}>
                        No tracks attached to this album.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="yt-studio-tracklist-modal-footer">
              <button
                className="yt-studio-dark-pill-btn"
                onClick={() => {
                  setSelectedStudioAlbum(null);
                  if (onNavigate) onNavigate('/albums');
                }}
              >
                View in Public Music Albums
              </button>
              <button
                className="yt-studio-white-pill-btn"
                onClick={() => setSelectedStudioAlbum(null)}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pop-up Confirm Modal for Album Deletion */}
      {(() => {
        const targetAlbum = albumsList.find((a) => a.id === deletingAlbumId || a.dbId === deletingAlbumId);
        const isTargetDenied = Boolean(targetAlbum?.isDenied);
        return (
          <ConfirmModal
            isOpen={Boolean(deletingAlbumId)}
            title={isTargetDenied ? "Remove denied album?" : "Delete music album?"}
            message={
              isTargetDenied
                ? "Are you sure you want to remove this denied album from your studio releases? This action cannot be undone."
                : "Are you sure you want to permanently delete this album and its tracklist? This action cannot be undone."
            }
            confirmText={isTargetDenied ? "Remove permanently" : "Delete permanently"}
            cancelText="Cancel"
            icon={MdOutlineDelete}
            danger={true}
            onConfirm={handleConfirmDeleteAlbum}
            onClose={() => setDeletingAlbumId(null)}
          />
        );
      })()}

      {/* YouTube Studio Live Subscriber Count Modal */}
      {showLiveSubModal && (
        <div className="yt-studio-live-sub-backdrop" onClick={() => setShowLiveSubModal(false)}>
          <div className="yt-studio-live-sub-modal" onClick={(e) => e.stopPropagation()}>
            <div className="yt-studio-live-sub-modal-header">
              <div className="yt-studio-live-sub-modal-title">Live subscriber count</div>
              <button
                type="button"
                className="yt-studio-live-sub-close"
                onClick={() => setShowLiveSubModal(false)}
                title="Close"
              >
                <MdClose size={22} />
              </button>
            </div>

            <div className="yt-studio-live-sub-channel-info">
              <div
                className="yt-studio-live-sub-avatar"
                style={{ backgroundColor: avatarColor }}
              >
                {avatarPreview ? (
                  <img src={avatarPreview} alt={profileName} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                ) : (
                  profileInitial
                )}
              </div>
              <div className="yt-studio-live-sub-channel-name">
                {customChannelName || profileName}
              </div>
              <div className="yt-studio-live-sub-channel-handle">
                {customChannelHandle || profileHandle}
              </div>
            </div>

            <div className="yt-studio-live-sub-counter-box">
              <div className="yt-studio-live-sub-counter-number">
                {dashboardData?.subscribersCount || 0}
              </div>
              <div className="yt-studio-live-sub-counter-label">
                Subscribers
              </div>
              <div className="yt-studio-live-sub-status-badge">
                <span className="yt-studio-live-dot" /> Updating live
              </div>
            </div>

            <div className="yt-studio-live-sub-footer-note">
              Telemetry reflects real-time database state across all users and devices.
            </div>
          </div>
        </div>
      )}

      {/* YouTube Studio Watch Time Breakdown by Content Modal */}
      {showWatchTimeModal && (
        <div className="yt-studio-live-sub-backdrop" onClick={() => setShowWatchTimeModal(false)}>
          <div className="yt-studio-watchtime-modal" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="yt-studio-watchtime-modal-header">
              <div>
                <div className="yt-studio-watchtime-modal-title">Watch time by content</div>
              </div>
              <button
                type="button"
                className="yt-studio-live-sub-close"
                onClick={() => setShowWatchTimeModal(false)}
                title="Close"
              >
                <MdClose size={22} />
              </button>
            </div>

            {/* Summary Stat Cards */}
            <div className="yt-studio-watchtime-summary-grid">
              <div className="yt-studio-watchtime-summary-card">
                <span className="yt-studio-wt-card-label">Total Watch Time</span>
                <span className="yt-studio-wt-card-val">
                  {(watchtimeBreakdown?.total_watch_time_hours ?? (dashboardData?.summary28Days?.watchTimeHours || 0)).toFixed(1)} hrs
                </span>
                <span className="yt-studio-wt-card-sub">
                  {watchtimeBreakdown?.total_watch_time_seconds 
                    ? `${Math.floor(watchtimeBreakdown.total_watch_time_seconds / 60)}m ${Math.floor(watchtimeBreakdown.total_watch_time_seconds % 60)}s total`
                    : 'Last 28 days'}
                </span>
              </div>
              <div className="yt-studio-watchtime-summary-card">
                <span className="yt-studio-wt-card-label">Videos</span>
                <span className="yt-studio-wt-card-val">
                  {(watchtimeBreakdown?.summary?.videos?.watch_time_hours ?? (dashboardData?.summary28Days?.watchTimeHours || 0)).toFixed(1)} hrs
                </span>
                <span className="yt-studio-wt-card-sub">
                  {watchtimeBreakdown?.summary?.videos?.count || 0} video(s)
                </span>
              </div>
              <div className="yt-studio-watchtime-summary-card">
                <span className="yt-studio-wt-card-label">Shorts</span>
                <span className="yt-studio-wt-card-val">
                  {(watchtimeBreakdown?.summary?.shorts?.watch_time_hours ?? 0).toFixed(1)} hrs
                </span>
                <span className="yt-studio-wt-card-sub">
                  {watchtimeBreakdown?.summary?.shorts?.count || 0} short(s)
                </span>
              </div>
              <div className="yt-studio-watchtime-summary-card">
                <span className="yt-studio-wt-card-label">Music Albums</span>
                <span className="yt-studio-wt-card-val">
                  {(watchtimeBreakdown?.summary?.albums?.watch_time_hours ?? 0).toFixed(1)} hrs
                </span>
                <span className="yt-studio-wt-card-sub">
                  {watchtimeBreakdown?.summary?.albums?.count || 0} album(s)
                </span>
              </div>
            </div>

            {/* Filter Tabs */}
            <div className="yt-studio-watchtime-filter-bar">
              <button
                type="button"
                className={`yt-studio-wt-filter-btn ${watchtimeFilter === 'all' ? 'active' : ''}`}
                onClick={() => setWatchtimeFilter('all')}
              >
                All Content ({watchtimeBreakdown?.items?.length || 0})
              </button>
              <button
                type="button"
                className={`yt-studio-wt-filter-btn ${watchtimeFilter === 'video' ? 'active' : ''}`}
                onClick={() => setWatchtimeFilter('video')}
              >
                Videos ({watchtimeBreakdown?.summary?.videos?.count || 0})
              </button>
              <button
                type="button"
                className={`yt-studio-wt-filter-btn ${watchtimeFilter === 'short' ? 'active' : ''}`}
                onClick={() => setWatchtimeFilter('short')}
              >
                Shorts ({watchtimeBreakdown?.summary?.shorts?.count || 0})
              </button>
              <button
                type="button"
                className={`yt-studio-wt-filter-btn ${watchtimeFilter === 'album' ? 'active' : ''}`}
                onClick={() => setWatchtimeFilter('album')}
              >
                Music Albums ({watchtimeBreakdown?.summary?.albums?.count || 0})
              </button>
            </div>

            {/* Items Content List */}
            <div className="yt-studio-watchtime-items-container">
              {isLoadingWatchtime ? (
                <div className="yt-studio-wt-loading">Loading watch time breakdown...</div>
              ) : (
                (() => {
                  const filtered = (watchtimeBreakdown?.items || []).filter(
                    item => watchtimeFilter === 'all' || item.type === watchtimeFilter
                  );
                  if (filtered.length === 0) {
                    return (
                      <div className="yt-studio-wt-empty">
                        No {watchtimeFilter === 'all' ? 'content' : watchtimeFilter + 's'} found with watch time data.
                      </div>
                    );
                  }
                  return (
                    <div className="yt-studio-wt-items-list">
                      {filtered.map((item) => (
                        <div key={item.id} className="yt-studio-wt-item-row">
                          <div className="yt-studio-wt-item-thumb-wrapper">
                            {item.thumbnailUrl ? (
                              <img src={item.thumbnailUrl} alt={item.title} className="yt-studio-wt-item-thumb" />
                            ) : (
                              <div className="yt-studio-wt-item-thumb-placeholder">
                                {item.type === 'album' ? <MdAlbum size={28} /> : <MdOutlineVideocam size={28} />}
                              </div>
                            )}
                            <span className="yt-studio-wt-duration-badge">{item.durationFormatted}</span>
                          </div>

                          <div className="yt-studio-wt-item-info">
                            <div className="yt-studio-wt-title-row">
                              <span className={`yt-studio-content-badge ${item.type}`}>
                                {item.typeLabel.toUpperCase()}
                              </span>
                              <h4 className="yt-studio-wt-item-title">{item.title}</h4>
                            </div>
                            <div className="yt-studio-wt-item-meta">
                              {item.artist && <span>{item.artist} &middot; </span>}
                              <span>{item.views.toLocaleString()} {item.viewsLabel || 'views'}</span>
                              <span> &middot; Published {item.createdDate ? new Date(item.createdDate).toLocaleDateString() : 'Recently'}</span>
                            </div>
                          </div>

                          <div className="yt-studio-wt-item-metrics">
                            <div className="yt-studio-wt-metric-hrs">
                              <strong>{(item.watchTimeHours || 0).toFixed(2)}</strong>
                              <span className="unit">hrs</span>
                            </div>
                            <div className="yt-studio-wt-metric-exact">
                              {item.watchTimeFormatted}
                            </div>
                            {item.avgViewDurationSec > 0 && (
                              <div className="yt-studio-wt-metric-retention">
                                Avg: {item.avgViewDurationSec}s / view
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()
              )}
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification Container */}
      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        icon={toast.icon}
        toastKey={toast.toastKey}
        onClose={() => setToast({ isOpen: false, message: '', icon: null })}
      />
    </div>
  );
}
